import type { FormEvent, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { collectPageKeys, applySelectedBatches } from '../lib/bulkSelection.js';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Camera,
  Check,
  ChevronDown,
  Eye,
  FileQuestion,
  Folder,
  FolderCog,
  FolderMinus,
  Link2,
  ScanLine,
  Mail,
  MessageCircle,
  MoreVertical,
  Paperclip,
  Pencil,
  Plus,
  Power,
  RefreshCw,
  Share2,
  Trash2,
  X,
} from 'lucide-react';
import type {
  ConnectedMailAccount,
  InvoiceDocType,
  InvoiceDocTypeFilter,
  InvoiceFolder,
  InvoiceList,
  InvoiceRecord,
  InvoiceShareInput,
  InvoiceSource,
  InvoiceSummary,
  MailSyncState,
} from '@worki/shared';
import {
  emailSchema,
  INVOICE_ARCHIVE_YEARS,
  INVOICE_DOC_TYPE_UNSET,
  invoiceArchiveStart,
  invoiceFolderSchema,
  invoiceShareSchema,
  LIMITS,
  needsMailboxLogin,
} from '@worki/shared';
import { api, buildQuery, RequestError, apiUrl } from '../lib/api.js';
import * as platform from '@platform';
import { useDebounced } from '../lib/useDebounced.js';
import { useIsPhone } from '../lib/useIsPhone.js';
import { useLongPress } from '../lib/useLongPress.js';
import { scanFinished, seenScan, type SeenScan } from '../lib/mailScan.js';
import { currentLocale } from '../i18n.js';
import { formatDate, formatTime, money } from '../lib/format.js';
import { Badge, Button, ErrorNotice, Field, Input, Select } from '../components/ui.js';
import { useForm } from '../lib/useForm.js';
import { ConfirmDialog, Modal } from '../components/Modal.js';
import { InvoiceScanDialog } from '../components/invoiceScan/InvoiceScanDialog.js';
import { InvoiceForwardingDialog } from '../components/invoiceForwarding/InvoiceForwardingDialog.js';
import { SelectionActionBar } from '../components/SelectionActionBar.js';
import { DetailDialog } from '../components/DetailDialog.js';
import { DetailRow, InvoiceDateRows } from '../components/InvoiceDetailRows.js';
import { ItemActions } from '../components/ItemActions.js';
import { AnchoredMenu } from '../components/AnchoredMenu.js';
import { InfoTip } from '../components/InfoTip.js';
import { MailScanPanel } from '../components/MailScan.js';
import { awaitingLogin, OAUTH_BUTTON, ReconnectMailboxDialog } from '../components/ReconnectMailboxDialog.js';
import { usePageSearch } from '../lib/pageSearch.js';
import { PeriodPicker, type Period } from '../components/PeriodPicker.js';
import { dashboardInvoicePeriod } from '../lib/dashboardInvoicePeriod.js';
import { PdfPreview } from '../components/PdfPreview.js';
import { GoogleMark, MicrosoftMark } from '../components/ProviderMarks.js';
import { useToast } from '../components/Toast.js';
import { cn } from '../lib/cn.js';

/** `POST /bulk/delete` applies row by row and names the rows it refused. */
interface BulkResult {
  deleted: number;
  failed: string[];
}

/**
 * `GET /invoices/accounts` — the mailboxes, and what this deployment can connect
 * them with. Named because it is written as well as read: a mailbox logged in
 * to again is put straight into it.
 */
interface MailAccountList {
  items: ConnectedMailAccount[];
  driver: string;
  oauthProviders: string[];
}

/** One row of an anchored menu — the actions menu and the type filter share it. */
const menuItem = 'w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-start transition-colors';

const PAGE_SIZE = 25;
const MAX_ACCOUNTS = 2;

/**
 * INV-006 — how far back the year picker offers to look.
 *
 * `INVOICE_ARCHIVE_YEARS`, the same four calendar years the mailbox scan reads
 * and the server lists, so every chip names a year the archive holds and no
 * row sits in a year without one. It was six, on the theory that books are kept
 * for seven years, and the two oldest chips opened an empty list. Counted from
 * the year the page is opened in rather than fixed, so it never goes stale.
 */
const YEAR_SPAN = INVOICE_ARCHIVE_YEARS;

/** The site a linked invoice came from. Falls back to the raw value it cannot parse. */
const hostOf = (url: string) => {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
};

/**
 * 1.10 — whether the page on screen is worth offering beside "select all".
 *
 * Not while the page is the whole answer: with one page of results it is
 * "select all" a second time, under another name. Not once the selection is
 * already exactly the page either, for the reason "select all" leaves the bar
 * when everything is selected. With the whole filter selected it stays: the
 * page is the smaller choice then, and pressing it narrows the selection.
 */
function pageChoiceOffered(rows: readonly InvoiceRecord[], total: number, selection: ReadonlySet<string>): boolean {
  if (rows.length === 0 || rows.length >= total) return false;
  return selection.size !== rows.length || !rows.every((row) => selection.has(row.id));
}

/**
 * 1.14 — the totals the bar over the list adds up: the ticked invoices' while
 * any are ticked, the filter's otherwise.
 *
 * `picked` is the server's sum of the selection. It is used only while
 * something is ticked, because the answer to a selection that was cleared can
 * still be lying around, and only once it covers something: until it arrives
 * the filter's figures stay up rather than a blank bar, and a selection whose
 * invoices have all gone (deleted on another device) adds up to nothing, which
 * is no reason to blank the bar either.
 *
 * `pickedCount` is counted off the answer, the invoices with no amount
 * included, and not off the ticks. The line above the figures has to describe
 * the set the figures are of, and for the moment the last selection's answer
 * stands in for the one just made, that is the answer's count.
 *
 * Section 5 — the invoices the business wrote are counted too: they are in the
 * answer apart from the expenses (`income`), and a selection of income alone is
 * still a selection.
 */
function summaryShown(
  selected: number,
  picked: InvoiceSummary | null | undefined,
  filtered: InvoiceSummary | undefined,
): { summary: InvoiceSummary | undefined; pickedCount: number } {
  if (selected > 0 && picked) {
    const pickedCount =
      picked.missingAmount +
      picked.byCurrency.reduce((sum, entry) => sum + entry.count, 0) +
      picked.income.reduce((sum, entry) => sum + entry.count, 0);
    if (pickedCount > 0) return { summary: picked, pickedCount };
  }
  return { summary: filtered, pickedCount: 0 };
}

/**
 * INV-001..008 — connected email accounts + the synced invoice list.
 *
 * A mailbox is connected in one of two ways and the dialog offers both: a
 * consent redirect to Google or Microsoft (INV-008), which is the one a
 * business owner can actually finish, and an IMAP server with an app password
 * for everything else — a company's own server, or a provider this deployment
 * has no client for. Which buttons appear is what the API reports as
 * configured, never a guess made here.
 *
 * The reference screen holds four hard-coded invoices in localStorage, so its
 * detail dialog is editable and its connect box is a button that does nothing.
 * Here the rows are real synced records with no update endpoint — an invoice is
 * a document someone else issued, so editing it would only desynchronise the
 * list from the mailbox. The dialog is therefore read-only, and the connect box
 * is wired to the real accounts, their sync state and the two-account cap.
 */
export function InvoicesPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const locale = currentLocale();

  const [connectOpen, setConnectOpen] = useState(false);
  const [forwardingOpen, setForwardingOpen] = useState(false);
  const [disconnectTarget, setDisconnectTarget] = useState<ConnectedMailAccount | null>(null);
  /* INV-010 — the mailbox the login dialog is open for. */
  const [reconnectTarget, setReconnectTarget] = useState<ConnectedMailAccount | null>(null);
  const [preview, setPreview] = useState<InvoiceRecord | null>(null);
  const [search, setSearch] = useState('');
  const [accountId, setAccountId] = useState('');
  /*
   * INV-004 — the archive opens on everything.
   *
   * It briefly opened on this month instead, so that the totals above it had a
   * period to total. That is the wrong trade: an owner looking for the invoice
   * they filed in March opens the screen, sees four documents, and concludes
   * the scan lost the rest. The summary reads whatever is filtered, so it says
   * something true either way — and the month is one tap up the screen.
   */
  const [period, setPeriod] = useState<Period>(() => dashboardInvoicePeriod(new URLSearchParams(window.location.search)));
  /*
   * INV-006 — the rows behind the "amount not read" warning, on their own.
   *
   * A count of invoices the parser could not read a total off is only half an
   * answer while the rows behind it cannot be reached: it is a number someone
   * has to act on, and acting means opening them.
   */
  const [missingOnly, setMissingOnly] = useState(false);
  /*
   * INV-007 — the archive narrowed to what the documents say they are.
   *
   * A set, not one value, because what people ask for is often two things — the
   * tax invoices and the receipts. It holds the filter's choices and not the
   * types behind them (1.5): `docTypeParamOf` turns the ticked ones into the
   * types the API is asked for. Empty means every type — the boxes all unticked
   * is the same view as never having opened the filter.
   */
  const [docTypes, setDocTypes] = useState<ReadonlySet<DocTypeOption>>(new Set());
  const [page, setPage] = useState(1);
  const [selection, updateSelection] = useState<Set<string>>(new Set());
  const selectionEpoch = useRef(0);
  const selectionReadContext = useRef('');
  const [allMatchingContext, setAllMatchingContext] = useState('');
  const setSelection = (next: Parameters<typeof updateSelection>[0]) => {
    selectionEpoch.current++;
    setAllMatchingContext('');
    updateSelection(next);
  };
  useEffect(() => () => { selectionEpoch.current++; }, []);
  const [selectMode, setSelectMode] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  /**
   * What the open share dialog is about to send, and the folder it came from
   * when it came from one.
   *
   * A share used to be the selection and nothing else, which made sending a
   * folder a three-step job: filter to it, select all of it, share. The ids are
   * held here instead so a folder can be handed over whole in one press, and
   * the folder travels with them so the dialog can say which one it is.
   */
  const [shareIds, setShareIds] = useState<string[]>([]);
  const [shareFolders, setShareFolders] = useState<InvoiceFolder[]>([]);
  /** The supplier of the single invoice sent from its own row, for the heading. */
  const [shareInvoiceName, setShareInvoiceName] = useState('');
  const [foldersOpen, setFoldersOpen] = useState(false);
  /**
   * 1.13 — whether the name for a new folder is being asked for, after "תיקייה
   * חדשה…" was chosen in the selection bar's folder menu. The selection stays as
   * it is until the folder has been made.
   */
  const [naming, setNaming] = useState(false);
  /**
   * A folder id, or empty for every invoice. Only the folder menu sets it, and
   * it lives nowhere else — not in the address, not in storage — so no old link
   * can bring back the unfiled pile the menu no longer offers (1.10).
   */
  const [folderId, setFolderId] = useState('');
  /** 10.1 — the folder whose bin was pressed in the folder menu, until it is answered. */
  const [folderToDelete, setFolderToDelete] = useState<InvoiceFolder | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  /** Section 3 — the quick scan, which is how an invoice is added by hand (section 6). */
  const [scanOpen, setScanOpen] = useState(false);
  const phone = useIsPhone();
  /**
   * 1.11 — whether a document on paper is scanned with a camera, as in "המסמכים
   * שלי": a phone is held up to the page, and the Android app is one at any width,
   * since a phone turned on its side is wider than the breakpoint and still has
   * its camera. A desktop has none, so its quick scan opens on a place to drop files.
   */
  const camera = phone || platform.handheld;
  /*
   * INV-006 — the page's own actions, gathered into one control.
   *
   * They used to be scattered across the screen: a connect strip above
   * everything, a delete-and-rescan under the accounts, a folders cog wedged
   * between two filters. Three different places to look for "something I do to
   * this page", none of them where a phone thumb naturally goes.
   */
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLButtonElement>(null);

  const debouncedSearch = useDebounced(search, 300);
  /*
   * The set is turned into a string here rather than passed as itself: a Set is
   * compared by identity, so a dependency list holding one re-runs on every
   * render, and the same boxes ticked in a different order are the same filter.
   */
  const docTypeParam = docTypeParamOf(docTypes);
  useEffect(() => setPage(1), [debouncedSearch, accountId, period, folderId, missingOnly, docTypeParam]);

  /*
   * INV-003 — this query is the progress feed as well as the account list.
   *
   * The scan state rides on the account row, so one poll drives the panel, its
   * counters, and the moment the invoice list is told to refetch. It polls only
   * while something is actually being read; the rest of the time it is the
   * static list it always was.
   */
  const accounts = useQuery({
    queryKey: ['invoice-accounts'],
    queryFn: () => api.get<MailAccountList>('/invoices/accounts'),
    refetchInterval: (query) =>
      query.state.data?.items.some((account) => account.sync?.status === 'running') ? 1_200 : false,
    // A scan outlives the user's attention. Left at the default, the interval
    // suspends the moment the tab goes to the background, so someone who checks
    // their mail while a mailbox is read comes back to a panel frozen where
    // they left it and a scan that finished minutes ago without ever saying so.
    refetchIntervalInBackground: true,
  });

  /*
   * What the screen is narrowed to, with no page in it.
   *
   * The list adds paging to this; picking up "everything here" asks the server
   * for the identities behind the very same filter. One object rather than two
   * call sites, because a filter the list applies and the selection does not is
   * a selection that covers invoices the user cannot see.
   */
  const filters = {
    q: debouncedSearch,
    accountId,
    range: period.range,
    month: period.month,
    // One key holding every chosen year, comma separated. `buildQuery` drops an
    // empty string, so "all years" sends nothing and the server keeps its own
    // default rather than being handed an empty list to interpret.
    years: period.years.join(','),
    from: period.from,
    to: period.to,
    // Sent only when it is on: the server reads the string, and `false`
    // arriving as text is a value the filter would have to know to
    // ignore. An absent parameter is unambiguous.
    ...(missingOnly ? { missingAmount: 'true' } : {}),
    docType: docTypeParam,
    folderId,
    sort: 'issuedAt',
    dir: 'desc',
  };

  selectionReadContext.current = JSON.stringify([search, filters, page]);

  /*
   * The rows on screen stay until the next ones arrive. Every letter typed into
   * the search is a new key, and without this each one emptied the list to a
   * line of "loading", dropped the totals and the pager, and filled it all back
   * in — the page collapsing and regrowing under the finger on every keystroke,
   * which on a slow phone reads as "no results". The search box shows a spinner
   * meanwhile.
   */
  const invoices = useQuery({
    queryKey: ['invoices', { debouncedSearch, accountId, period, folderId, missingOnly, docTypeParam, page }],
    queryFn: () => api.get<InvoiceList>(`/invoices${buildQuery({ ...filters, page, pageSize: PAGE_SIZE })}`),
    placeholderData: keepPreviousData,
  });
  /* UI-11 — the magnifier moved to the bar, beside the name of the screen. It
     spins from the keystroke until the rows for that term are on screen. */
  usePageSearch(
    search,
    setSearch,
    t('invoices.searchPlaceholder'),
    true,
    search !== debouncedSearch || (invoices.isFetching && invoices.isPlaceholderData),
  );

  /*
   * 1.14 — what the ticked invoices add up to, for the bar over the list.
   *
   * The selection can reach across pages and the browser holds one of them, so
   * the sum is the server's. The key sits under `invoices`, so whatever
   * refreshes the list — a finished scan, a filing, coming back to the tab —
   * refreshes this too, and the ids go in sorted: the same ticks in another
   * order are the same question.
   *
   * Two things keep the bar from showing the wrong set. `keepPreviousData`
   * holds the last answer while the next is on its way, so the bar does not
   * jump to the filter's figures and back on every tick. And nothing ticked is
   * answered with `null` rather than left unasked: a query that never answered
   * has no data to hold, and the next selection would wait behind the figures
   * of the last one that had some.
   */
  const selectedIds = [...selection].sort();
  const selectedSummary = useQuery({
    queryKey: ['invoices', 'selection-summary', selectedIds],
    queryFn: async () => {
      if (selectedIds.length === 0) return null;
      return api.post<InvoiceSummary>('/invoices/summary', { ids: selectedIds });
    },
    placeholderData: keepPreviousData,
  });

  /*
   * INV-006 — take the whole filter, not the page that fits on screen.
   *
   * Reported from a real archive: a folder of sixty-four invoices, "select all"
   * picking up the twenty-five that were showing, and no way to send the folder
   * to an accountant. The ids come from the server because the browser only
   * ever held one page of them.
   */
  const selectEverything = useMutation({
    mutationFn: async (capture: { epoch: number; context: string; filters: typeof filters }) => {
      const keys = await collectPageKeys(async (page) => {
        if (capture.epoch !== selectionEpoch.current || capture.context !== selectionReadContext.current) throw new Error('Selection cancelled');
        const result = await api.get<InvoiceList>(`/invoices${buildQuery({ ...capture.filters, page, pageSize: 100 })}`);
        if (result.total > 10000) throw new Error('common.bulkSelectionLimit');
        return result;
      }, (row) => row.id);
      return keys;
    },
    onSuccess: (keys, capture) => {
      if (capture.epoch !== selectionEpoch.current || capture.context !== selectionReadContext.current) return;
      setSelection(new Set(keys));
      setAllMatchingContext(capture.context);
      setSelectMode(keys.length > 0);
    },
    onError: (error, capture) => {
      if (capture.epoch === selectionEpoch.current && capture.context === selectionReadContext.current) toast.error(t(error.message === 'common.bulkSelectionLimit' ? error.message : 'errors.network', { count: 10000 }));
    },
  });

  const folders = useQuery({
    queryKey: ['invoice-folders'],
    queryFn: () => api.get<{ items: InvoiceFolder[] }>('/invoices/folders'),
  });

  /*
   * INV-006 — hand a folder over whole, from the folder itself.
   *
   * Asked for by the person who files these: a folder is the batch that gets
   * sent, and reaching it through "filter, select all, share" is three steps to
   * express one intent — and the middle one is invisible until you know a row
   * can be held down. So the folder list offers it directly.
   *
   * The ids come from the server for the same reason "select all" does: the
   * browser only ever held one page of them. The period filter is deliberately
   * not applied — the folder is the whole answer here, and quietly sending a
   * third of it because the screen behind was showing August is the failure
   * this is meant to remove.
   */
  const shareFolderContents = useMutation({
    mutationFn: async (chosen: InvoiceFolder[]) => {
      const pages = await Promise.all(
        chosen.map((folder) =>
          api.get<{ ids: string[]; truncated: boolean }>(
            `/invoices/ids${buildQuery({ folderId: folder.id, range: 'all', sort: 'issuedAt', dir: 'desc' })}`,
          ),
        ),
      );
      // A Set because two folders cannot hold the same invoice today, and a
      // duplicate id would put the same page in the document twice if they ever
      // could.
      const ids = [...new Set(pages.flatMap((page) => page.ids))];
      return {
        folders: chosen,
        ids,
        truncated: pages.some((page) => page.truncated) || ids.length > LIMITS.maxBulkInvoices,
      };
    },
    onSuccess: ({ ids, truncated, folders: chosen }) => {
      if (truncated) { toast.error(t('common.bulkShareLimit', { count: LIMITS.maxBulkInvoices })); return; }
      setShareIds(ids);
      setShareFolders(chosen);
      setShareInvoiceName('');
      setFoldersOpen(false);
      setShareOpen(true);
    },
  });

  /*
   * INV-006 — file the selection.
   *
   * A move is not a delete and does not clear the selection the way one does:
   * filing a batch and then sharing the same batch is one job, and reselecting
   * fourteen rows to do the second half of it is not.
   */
  const move = useMutation({
    mutationFn: async (target: string) => {
      selectionEpoch.current++;
      const ids = [...selection];
      return applySelectedBatches(ids, async (batch) => {
        const result = await api.post<{ moved: number }>('/invoices/bulk/folder', { ids: batch, folderId: target === 'none' ? null : target });
        return { ok: result.moved, failed: [] };
      }, LIMITS.maxBulkInvoices);
    },
    onSuccess: (result) => {
      invalidateAll();
      if (result.failed.length) toast.error(t('errors.unknown'));
      else toast.success(t('invoices.moved', { count: result.ok }));
    },
    // A refused move used to say nothing, which left invoices someone had just
    // filed simply not in the folder they filed them into.
    onError: (failure) => toast.error(t(failure instanceof RequestError ? failure.code : 'errors.unknown')),
  });

  const invalidateAll = () => {
    void queryClient.invalidateQueries({ queryKey: ['invoice-accounts'] });
    void queryClient.invalidateQueries({ queryKey: ['invoices'] });
    // The counts on the folders are a fact about the invoices, so anything that
    // moves an invoice moves them too.
    void queryClient.invalidateQueries({ queryKey: ['invoice-folders'] });
  };

  /*
   * 1.3 — a folder renamed from the folder filter, where the pencil is.
   *
   * It settles once the folders have been read again, joining the read that
   * `invalidateAll` starts rather than starting a second one, so the filter can
   * keep the typed name on its row until the list has it instead of showing the
   * old name in between. A refusal, such as a name another folder already has,
   * rejects with the server's code for the filter to show.
   */
  const renameFolder = async (folder: InvoiceFolder, name: string) => {
    await api.put<InvoiceFolder>(`/invoices/folders/${folder.id}`, { name });
    invalidateAll();
    await queryClient.refetchQueries({ queryKey: ['invoice-folders'] }, { cancelRefetch: false });
  };

  /*
   * 1.10 — a folder made from the folder filter, by the plus beside "תיקיות".
   *
   * The request the manage dialog sends. It settles once the folders have been
   * read again, so the new one is on the menu when its field closes rather than
   * a moment later. A refusal, such as a name another folder already has,
   * rejects with the server's code for the filter to show. A new folder holds
   * nothing, so only the folders are read again.
   *
   * 1.13 — it settles with the folder the server made, which the selection bar's
   * "תיקייה חדשה…" files the selection into. The filter has no use for it.
   */
  const createFolder = async (name: string) => {
    const folder = await api.post<InvoiceFolder>('/invoices/folders', { name });
    await queryClient.invalidateQueries({ queryKey: ['invoice-folders'] });
    return folder;
  };

  /*
   * After the manage dialog makes, renames, empties or deletes a folder, and
   * after the bin in the folder menu deletes one. A list narrowed to a folder
   * that no longer exists would otherwise sit on an empty page with no way back
   * to the invoices.
   */
  const foldersChanged = (deletedId?: string) => {
    invalidateAll();
    if (deletedId && deletedId === folderId) setFolderId('');
  };

  /*
   * 1.10 — the same for a folder deleted on another device, once the folders
   * are read again without it. Until then the control reads "תיקיות", since
   * the id names no folder it has, while the list stays narrowed to the folder
   * that went and so shows nothing.
   */
  useEffect(() => {
    if (folderId && folders.data && !folders.data.items.some((folder) => folder.id === folderId)) setFolderId('');
  }, [folderId, folders.data]);

  /*
   * 10.1 — a folder deleted from the folder filter, by the bin beside its share
   * icon, once the question it asks has been agreed to.
   *
   * The request the manage dialog sends. The server takes the invoices out of
   * the folder and deletes none of them (`deleteFolder` in the API's
   * invoiceFolders service), which is what the question says.
   */
  const removeFolder = useMutation({
    mutationFn: (folder: InvoiceFolder) => api.del(`/invoices/folders/${folder.id}`),
    onSuccess: (_result, folder) => {
      setFolderToDelete(null);
      foldersChanged(folder.id);
      toast.success(t('common.deleted'));
    },
    onError: (failure) => {
      setFolderToDelete(null);
      toast.error(t(failure instanceof RequestError ? failure.code : 'errors.unknown'));
    },
  });

  /*
   * INV-008 — what the consent screen sent the user back with.
   *
   * Connecting a mailbox leaves the app entirely, so the outcome cannot arrive
   * as a mutation result; it comes back as a query parameter on the URL the
   * provider redirected to. It is announced once and then taken off the
   * address, so a reload is not a second announcement.
   */
  const [params, setParams] = useSearchParams();
  const announced = useRef(false);
  useEffect(() => {
    const connected = params.get('mailbox');
    const failure = params.get('mailboxError');
    if (!connected && !failure) return;
    // StrictMode mounts effects twice in development, and the account was
    // connected once.
    if (announced.current) return;
    announced.current = true;

    if (connected === 'connected' || connected === 'reconnected') {
      // INV-010 — the same trip, taken to log in to a mailbox again. The server
      // updated the account it already had and resumed its scan, so that is
      // what is announced, and not a mailbox added.
      toast.success(t(connected === 'reconnected' ? 'invoices.accountReconnected' : 'invoices.accountConnected'));
      void queryClient.invalidateQueries({ queryKey: ['invoice-accounts'] });
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
    } else {
      /*
       * Only Worki's own keys are translated. This value came off a URL, and
       * `t()` prints a key it does not recognise as itself — without the check
       * a crafted link could put any sentence its author liked on the screen,
       * inside the app's own error notice.
       */
      const known = failure !== null && /^(invoices|errors)\.[A-Za-z]+$/.test(failure);
      toast.error(t(known ? (failure as string) : 'invoices.oauthFailed'));
    }

    const next = new URLSearchParams(params);
    next.delete('mailbox');
    next.delete('mailboxError');
    setParams(next, { replace: true });
  }, [params, setParams, toast, t, queryClient]);

  /*
   * The same outcome, for a shell that watched the flow happen somewhere else.
   *
   * Consent ran in the system browser, so the callback's redirect lands there
   * and this app is never told. What does come back is the app itself, so the
   * accounts are refetched whenever it returns to the foreground and the new
   * mailbox appears on its own. Cheap enough to do unconditionally: it is one
   * small query, and only the packaged shells register it at all.
   */
  useEffect(() => {
    if (platform.mailboxOauth !== 'system-browser') return;
    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      void queryClient.invalidateQueries({ queryKey: ['invoice-accounts'] });
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
    };
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [queryClient]);

  /*
   * Starts the scan; it does not wait for it. The response is the scan's opening
   * state, and refetching the accounts turns the row into the panel at once —
   * everything after that comes from the poll above.
   */
  const sync = useMutation({
    /*
     * Resumes from the last sync's watermark. The API also takes `?full=1` to
     * walk the whole four-year window again; the row used to offer that as a
     * second button and no longer does — "delete all and rescan" in the page
     * menu is the way back to a mailbox's history, and the server starts that
     * one as a full walk itself.
     */
    mutationFn: ({ id }: { id: string }) => api.post<MailSyncState>(`/invoices/accounts/${id}/sync`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invoice-accounts'] }),
    onError: () => toast.error(t('errors.unknown')),
  });

  /*
   * What the poll means, once per change.
   *
   * Invoices are written as they are found, so the list is refetched the moment
   * the imported count moves rather than at the end — that is what puts rows on
   * the screen while the mailbox is still being read. The closing toast fires
   * once for each scan that finished since the last look — see `scanFinished`,
   * which also counts a scan over too fast for any poll to see it running — and
   * a scan that ended before the page loaded is not announced at all.
   */
  const lastScan = useRef(new Map<string, SeenScan>());

  useEffect(() => {
    for (const account of accounts.data?.items ?? []) {
      const scan = account.sync;
      const previous = lastScan.current.get(account.id);
      lastScan.current.set(account.id, seenScan(scan));
      if (!scan || !previous) continue;

      if (scan.imported !== previous.imported) {
        void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      }
      if (scanFinished(previous, scan)) {
        void queryClient.invalidateQueries({ queryKey: ['invoices'] });
        if (scan.status === 'error' && needsMailboxLogin(scan.errorCode)) {
          // INV-010 — a failure only a login fixes is answered with the login.
          // It used to be a toast saying so, gone in four seconds and leaving
          // nothing to press. A dialog already open for the other mailbox is
          // left as it is, half typed; this one's row says what it needs.
          setReconnectTarget((open) => open ?? account);
        } else if (scan.status === 'error') {
          toast.error(t(scan.errorCode ?? 'errors.unknown'));
        } else if (scan.imported === 0) {
          // A plural of zero reads as a failure — "0 new invoices synced" is
          // what a scan that never ran would say too.
          toast.success(t('invoices.syncedNone'));
        } else {
          toast.success(t('invoices.synced', { count: scan.imported }));
        }
      }
    }
  }, [accounts.data, queryClient, t, toast]);

  /*
   * INV-010 — a mailbox logged in to again from the dialog.
   *
   * The answer is the account as the server now has it, its scan already
   * started, and it goes straight into the list rather than waiting for the
   * refetch: the row becomes the scan panel at once instead of offering the
   * login for another moment, and the poll that follows the scan starts with it.
   */
  const reconnected = (account: ConnectedMailAccount) => {
    queryClient.setQueryData<MailAccountList>(
      ['invoice-accounts'],
      (list) => list && { ...list, items: list.items.map((item) => (item.id === account.id ? account : item)) },
    );
    void queryClient.invalidateQueries({ queryKey: ['invoice-accounts'] });
    toast.success(t('invoices.accountReconnected'));
  };

  /*
   * What disconnecting is about to delete, counted before it is agreed to.
   *
   * Disconnecting takes the mailbox's invoices with it, and there is no undo —
   * so the confirmation has to name the number rather than say "its invoices"
   * and leave someone approving a figure they cannot see. One row is asked for,
   * because it is the total that is wanted and not the page.
   */
  const disconnectCount = useQuery({
    queryKey: ['invoices', 'disconnect-count', disconnectTarget?.id],
    queryFn: () =>
      api.get<InvoiceList>(
        `/invoices${buildQuery({ range: 'all', accountId: disconnectTarget?.id, page: 1, pageSize: 1 })}`,
      ),
    enabled: disconnectTarget !== null,
  });

  const disconnect = useMutation({
    mutationFn: (id: string) => api.post(`/invoices/accounts/${id}/disconnect`),
    onSuccess: () => {
      setDisconnectTarget(null);
      invalidateAll();
    },
  });

  const bulkDelete = useMutation({
    mutationFn: async () => {
      selectionEpoch.current++;
      const attempted = [...selection];
      const unfile = Boolean(folderId);
      const result = await applySelectedBatches(attempted, async (ids) => {
        if (folderId) {
          const moved = await api.post<{ moved: number }>('/invoices/bulk/folder', { ids, folderId: null });
          return { ok: moved.moved, failed: [] };
        }
        const deleted = await api.post<BulkResult>('/invoices/bulk/delete', { ids });
        return { ok: deleted.deleted, failed: deleted.failed };
      }, LIMITS.maxBulkInvoices);
      return { ...result, attempted, unfile };
    },
    onSuccess: (result) => {
      setConfirmDelete(false);
      setSelection((current) => new Set([...current].filter(id => !result.attempted.includes(id) || result.failed.includes(id))));
      invalidateAll();
      if (result.failed.length) toast.error(t('invoices.deleteSomeRefused', { count: result.failed.length }));
      else toast.success(result.unfile ? t('invoices.moved', { count: result.ok }) : t('common.deleted'));
    },
    onError: () => {
      setConfirmDelete(false);
      toast.error(t('errors.unknown'));
    },
  });

  /*
   * The destructive twin of the rescan button. A scan only ever adds, so a row
   * imported under an older rule stays until something removes it — this empties
   * the list and reads every connected mailbox again in one move.
   */
  const reset = useMutation({
    mutationFn: () => api.post<{ deleted: number }>('/invoices/reset'),
    onSuccess: (result) => {
      setConfirmReset(false);
      setSelection(new Set());
      setSelectMode(false);
      invalidateAll();
      toast.success(t('invoices.resetAllDone', { count: result.deleted }));
    },
    onError: () => {
      setConfirmReset(false);
      toast.error(t('errors.unknown'));
    },
  });

  const toggleSelect = (id: string) => {
    if (!selection.has(id) && selection.size >= 10000) { toast.error(t('common.bulkSelectionLimit', { count: 10000 })); return; }
    setSelection((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /*
   * INV-00x — holding a row is what asks for the checkboxes, and the gesture
   * itself lives in `useLongPress`: the same hold opens the same checkboxes in
   * "my documents", and a second copy of a timer that has to tell a press from
   * a scroll is a second copy that drifts.
   */
  const longPress = useLongPress((id) => {
    setSelectMode(true);
    setSelection(new Set([id]));
  });

  const connected = (accounts.data?.items ?? []).filter((account) => account.status !== 'disconnected');
  const atCap = connected.length >= MAX_ACCOUNTS;
  const rows = invoices.data?.items ?? [];
  const total = invoices.data?.total ?? 0;
  /*
   * INV-006 — the totals describe the chosen period, not the page: they come
   * off the API beside the rows rather than being added up from `rows`, which
   * holds 25 of them.
   *
   * 1.14 — while anything is ticked they describe the ticked invoices instead,
   * wherever in the list those are. `filterSummary` stays the period's own, for
   * the warning that speaks about the list.
   */
  const filterSummary = invoices.data?.summary;
  const { summary, pickedCount } = summaryShown(selection.size, selectedSummary.data, filterSummary);
  const summaryInvoiceCount=summary ? [...summary.byCurrency,...summary.income].reduce((n,e)=>n+e.count,summary.missingAmount) : 0;
  const singleSummaryCurrency=summary ? summary.byCurrency.length+summary.income.length===1 : false;
  const folderList = folders.data?.items ?? [];
  /*
   * 1.12 — the folder the summary is of, which the card names. It is read off
   * the list on every render rather than kept from the moment it was chosen, so
   * a rename shows as soon as it is read back; and an id that names nothing on
   * the list is no folder, as it is in the menu, so the card says nothing.
   */
  const chosenFolder = folderList.find((folder) => folder.id === folderId);
  const folderHasHiddenInvoices = chosenFolder && !invoices.isLoading && !invoices.isPlaceholderData && chosenFolder.count > total;
  const showAllInFolder = () => {
    setSearch('');
    setAccountId('');
    setPeriod({ range: 'all', month: '', years: [], from: '', to: '' });
    setDocTypes(new Set());
    setMissingOnly(false);
    setPage(1);
    setSelection(new Set());
  };
  const thisYear = new Date().getFullYear();
  const years = Array.from({ length: YEAR_SPAN }, (_, index) => thisYear - index);
  const archiveFirstDay = `${invoiceArchiveStart().getFullYear()}-01-01`;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  /*
   * A list that shrinks under the page on screen takes the page back with it.
   *
   * Filing the last invoices of a folder somewhere else, or deleting them, left
   * the screen on a page past the end: an empty list, and no pager to leave it
   * by once the rest fit on one page — so what was still in the folder looked
   * lost along with what was filed ("הם לא נשארות בליסט"). The check waits for
   * an answer, so a page still loading is not mistaken for a list of none.
   */
  useEffect(() => {
    if (invoices.data && page > pages) setPage(pages);
  }, [invoices.data, page, pages]);

  /*
   * Whether the selection already covers the filter — what "select all"
   * toggles. Measured against the total rather than the page, because the
   * button now takes the whole filter and a label offering to select what is
   * already selected is a label that lies.
   */
  const allSelected = total > 0 && allMatchingContext === selectionReadContext.current && selection.size === total;

  const amountOf = (row: InvoiceRecord) =>
    row.amount !== null && row.currency ? money(row.amount, row.currency, locale) : null;

  /*
   * INV-006 — the file onto the device.
   *
   * This used to be `window.open(...)` on the row and a `target="_blank"` link
   * in the dialog, and both are a request made by a browsing context this app
   * does not control: a blocked popup did nothing and said nothing, and a
   * download handed to a phone's own downloader arrives without the session
   * cookie and fails as a system notification. `api.download` fetches it here,
   * with the cookie, and a failure becomes a toast that names it.
   */
  const saveInvoice = async (invoice: InvoiceRecord) => {
    try {
      await api.download(
        `/invoices/${invoice.id}/download`,
        `${invoice.supplier ?? 'invoice'}-${invoice.number}.pdf`,
      );
    } catch (error) {
      toast.error(t(error instanceof RequestError ? error.code : 'errors.unknown'));
    }
  };

  /*
   * Everything this page does to itself, in one control. It is drawn beside
   * the mailbox it does most of it to, and on its own line only while there is
   * no mailbox yet — a line of a phone screen spent on a single button reads
   * as a heading for nothing.
   */
  const actionsMenu = (
    <>
      <button
        type="button"
        ref={menuRef}
        onClick={() => setMenuOpen((was) => !was)}
        aria-label={t('common.actions')}
        aria-expanded={menuOpen}
        className="h-9 w-9 grid place-items-center rounded-lg hover:bg-muted transition-colors"
      >
        <MoreVertical className="h-5 w-5 text-muted-foreground" />
      </button>
      <AnchoredMenu open={menuOpen} onClose={() => setMenuOpen(false)} anchorRef={menuRef} align="end" width={228}>
        <button
          type="button"
          onClick={() => { setMenuOpen(false); setForwardingOpen(true); }}
          className={cn(menuItem, 'hover:bg-muted')}
        >
          <Mail className="h-4 w-4 text-primary shrink-0" />
          {t('invoices.forwarding.title')}
        </button>
        <button
          type="button"
          disabled={atCap}
          onClick={() => {
            setMenuOpen(false);
            setConnectOpen(true);
          }}
          className={cn(menuItem, 'hover:bg-muted disabled:opacity-40 disabled:pointer-events-none')}
        >
          <Link2 className="h-4 w-4 text-primary shrink-0" />
          <span className="min-w-0">
            <span className="block truncate">{t('invoices.connectAccount')}</span>
            {atCap && (
              <span className="block text-[10px] text-muted-foreground truncate">
                {t('invoices.maxAccountsReached')}
              </span>
            )}
          </span>
        </button>
        {/* Section 6 — the one way to add an invoice by hand, with the camera or
            as files: "הוספת חשבונית", which took a typed invoice, is gone. Every
            file is an invoice of its own, and they are checked on one screen
            before they are filed (section 3). */}
        <button
          type="button"
          onClick={() => {
            setMenuOpen(false);
            setScanOpen(true);
          }}
          className={cn(menuItem, 'hover:bg-muted')}
        >
          <ScanLine className="h-4 w-4 text-primary shrink-0" />
          {t('scan.title')}
        </button>
        <button
          type="button"
          onClick={() => {
            setMenuOpen(false);
            setFoldersOpen(true);
          }}
          className={cn(menuItem, 'hover:bg-muted')}
        >
          <FolderCog className="h-4 w-4 text-muted-foreground shrink-0" />
          {t('invoices.manageFolders')}
        </button>
        {/* Last and apart: it empties the archive. Offered only when there is
            a mailbox to rescan afterwards — on a page with none it would
            delete the uploaded invoices and start nothing. */}
        {connected.length > 0 && (
          <button
            type="button"
            disabled={reset.isPending}
            onClick={() => {
              setMenuOpen(false);
              setConfirmReset(true);
            }}
            className={cn(
              menuItem,
              'border-t border-border text-destructive hover:bg-destructive/10 disabled:opacity-50',
            )}
          >
            <Trash2 className="h-4 w-4 shrink-0" />
            {t('invoices.resetAll')}
          </button>
        )}
      </AnchoredMenu>
    </>
  );

  return (
    <div className="animate-fade-up max-w-3xl mx-auto">
      {/*
        Everything this page does to itself, in one control beside the mailbox
        it does most of it to — the corner a thumb reaches on a phone, and the
        corner every other overflow menu in the product already lives in. It
        used to sit on a line of its own above the account, which spent a row of
        a phone screen on a single button and read as a heading for nothing.
      */}
      {connected.length === 0 && <div className="flex items-center justify-end mb-3">{actionsMenu}</div>}

      {/*
        The way in, and only while there is no way in yet.
        Connecting a mailbox is the first thing this page is for, and a business
        that has never done it would otherwise open an empty screen whose only
        affordance is three dots. Once a mailbox is connected the strip has said
        everything it has to say, and connecting a second one lives in the menu
        with the rest of the page's actions.
      */}
      {connected.length === 0 && (
        <div className="glass-card rounded-xl px-4 py-2.5 mb-4 border-primary/20 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Link2 className="h-4 w-4 text-primary shrink-0" />
            <div className="min-w-0">
              <h3 className="text-xs font-semibold">{t('invoices.connectAccount')}</h3>
              <p className="text-[10px] text-muted-foreground truncate">
                {t('invoices.connectHint', { max: MAX_ACCOUNTS })}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setConnectOpen(true)}
            className="px-3 py-1 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:opacity-90 transition-opacity active:scale-[0.97] shrink-0"
          >
            {t('invoices.connect')}
          </button>
        </div>
      )}

      {/*
        INV-006 — what the chosen period cost, and the VAT part of it the
        business is credited for. One card per currency: adding shekels to euros
        gives a number that is wrong in both, and a mailbox that has bought
        anything from abroad has both in it.

        An invoice whose amount never parsed is counted out loud instead of
        being read as zero — a total that is short has to say it is short,
        because this is the figure someone files a return against.
      */}
      {/*
        `missingOnly` keeps the card on screen when the totals are empty, and
        they are always empty under that filter — these are the rows with no
        amount on them. Without it the card would vanish the moment the warning
        was pressed, taking the only way back off the filter with it.

        1.14 — a selection keeps it on screen for the same reason: ticked
        invoices whose amounts were never read add up to no currency at all, and
        the bar still has to say how many are ticked and why there is no total.
      */}
      {summary && (summary.byCurrency.length > 0 || summary.income.length > 0 || summary.missingAmount > 0 || missingOnly || pickedCount > 0) && (
        <div className="mb-3 rounded-xl border border-primary/20 bg-primary/5 p-3 space-y-2">
          {/*
            1.12 — whose figures these are. The folder is chosen in a menu that
            closes behind the choice, and what stays on screen is a total that
            does not say what it is the total of. The name sits at the top of
            the card and wraps rather than being cut: it is sixty characters at
            most, and cutting it would hide the one thing this line is for.

            A paragraph and not a div, because the rows below rely on being the
            first div: that is what lets the first currency skip the rule, and
            keeps it from being drawn between that currency and this name.
          */}
          {chosenFolder && (
            <p className="flex items-start gap-1.5 text-sm font-semibold">
              <Folder className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
              <span className="min-w-0 break-words">{chosenFolder.name}</span>
            </p>
          )}
          {/*
            1.14 — whose figures these are once invoices are ticked: theirs, and
            not the filter's or the folder's. It sits under the folder's name
            when there is one, because what follows is the ticked invoices' sum
            and the name is only where they came from. A paragraph, like the
            name, so the first currency row is still the first div.
          */}
          {pickedCount > 0 && (
            <p className="text-xs font-semibold text-primary">{t('invoices.summarySelected', { count: pickedCount })}</p>
          )}
          {/*
            INV-013 — the same three parts, ordered by how wide the screen is.

            On a desktop they sit on one line: total, credit, and the count
            pushed to the far end. On a phone that line wraps, and it used to
            wrap badly: the count kept `ms-auto`, so it landed alone at the
            start of whatever line was left over — under the euro figure for
            one currency, beside the shekel credit for the next. Three
            currencies came out three different shapes, and none of them said
            which count belonged to which money.

            So the phone gets a deliberate shape instead of a leftover one: the
            count rides on the total's line where it can be read against it,
            and the credit takes a line of its own. The rule between currencies
            is there only at that width, where each one is two lines and the
            spacing alone no longer says where one ends.
          */}
          {!singleSummaryCurrency && <p className="text-xs text-muted-foreground">{t('invoices.invoiceCount',{count:summaryInvoiceCount})}</p>}
          {summary.byCurrency.map((entry) => (
            <div
              key={entry.currency}
              className="flex flex-col gap-1 border-t border-primary/15 pt-2 first-of-type:border-t-0 first-of-type:pt-0"
            >
              <div className="flex items-baseline gap-2">
                
                <span className="text-lg font-semibold ltr-nums">{money(entry.total, entry.currency, locale)}</span><span className="text-xs text-muted-foreground">{t('invoices.fromInvoiceCount',{count:singleSummaryCurrency?summaryInvoiceCount:entry.count})}</span>
                <InfoTip label={t(`currencies.${entry.currency}`)}>
                  {t('currencies.description', { code: entry.currency, name: t(`currencies.${entry.currency}`) })}
                </InfoTip>
              </div>
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="basis-full text-xs text-muted-foreground">
                  {t('invoices.vatCredit')}:
                </span>
                <span className="text-sm font-medium text-primary ltr-nums">
                  {money(entry.vat, entry.currency, locale)}
                </span>
                <span className="text-[11px] text-muted-foreground ltr-nums">
                  {t('invoices.vatCreditHint', { rate: summary.vatRate })}
                </span>
                {/*
                  INV-007 — the credit is drawn from the tax invoices only, and
                  the gap between that and what the period cost is usually
                  large enough that a bare figure looks like a mistake. Naming
                  the documents it rests on is what turns "why is this so
                  small" into an answer.
                */}
                {entry.deductibleCount < entry.count && (
                  <span className="text-[11px] text-muted-foreground">
                    {t('invoices.vatCreditFrom', {
                      count: entry.deductibleCount,
                      total: entry.count,
                    })}
                  </span>
                )}
              </div>
              
            </div>
          ))}
          {/*
            Section 5 — what came in, beside what went out. The figures above
            are expenses only and stay exactly as they were; income documents
            are added up here instead, in green, with the output VAT they carry
            (מע״מ עסקאות) rather than a credit. Same three parts and the same
            phone shape as an expense row, so a currency reads the same way
            whichever side of the books it is on; there is no row at all for a
            business that has not filed any income.
          */}
          {summary.income.map((entry) => (
            <div
              key={`income-${entry.currency}`}
              className="flex flex-wrap items-baseline gap-x-4 gap-y-1 border-t border-success/20 pt-2 first-of-type:border-t-0 first-of-type:pt-0 sm:border-t-0 sm:pt-0"
            >
              <div className="flex items-baseline gap-2">
                <span className="text-xs text-success">{t('invoices.incomeTotal')} · {t('invoices.invoiceCount', { count: entry.count })}</span>
                <span className="text-lg font-semibold text-success ltr-nums">
                  {money(entry.total, entry.currency, locale)}
                </span>
                <InfoTip label={t(`currencies.${entry.currency}`)}>
                  {t('currencies.description', { code: entry.currency, name: t(`currencies.${entry.currency}`) })}
                </InfoTip>
              </div>
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="text-xs text-success">{t('invoices.incomeVat')}</span>
                <span className="text-sm font-medium text-success ltr-nums">
                  {money(entry.vat, entry.currency, locale)}
                </span>
                <span className="text-[11px] text-muted-foreground ltr-nums">
                  {t('invoices.vatCreditHint', { rate: summary.vatRate })}
                </span>
              </div>
              
            </div>
          ))}
          {/*
            The count was already here; what it was missing was the way in. A
            figure someone has to act on is only actionable when the rows behind
            it can be opened — so the warning is the control that opens them,
            and says so rather than leaving a bare sentence looking clickable.
          */}
          {summary.missingAmount > 0 && !missingOnly && pickedCount === 0 && (
            <button
              type="button"
              onClick={() => setMissingOnly(true)}
              className="flex items-center gap-2 text-[11px] text-warning hover:underline text-start"
            >
              <span>{t('invoices.missingAmount', { count: summary.missingAmount })}</span>
              {/* Counted as well, because Hebrew inflects the pronoun: one
                  invoice is shown, several are. */}
              <span className="font-semibold shrink-0">
                {t('invoices.missingAmountShow', { count: summary.missingAmount })}
              </span>
            </button>
          )}
          {/*
            1.14 — the same warning over the ticked invoices, without the way in.
            "Show them" narrows the list to the invoices with no amount, and the
            list is not what this is about: a total that is short still has to
            say so, but the button would open rows other than the ticked ones.
          */}
          {summary.missingAmount > 0 && !missingOnly && pickedCount > 0 && (
            <p className="text-[11px] text-warning">{t('invoices.missingAmount', { count: summary.missingAmount })}</p>
          )}
          {/*
            The disclaimer is there whenever the figure is: the credit above is
            worked out from what the scan could read and classify, and it is a
            number someone might otherwise carry straight into a return. It
            replaced a count of documents the scan had not classified yet — a
            sentence about Worki's own backlog that nobody could act on, where
            this one says what the figure is good for. The allocation line
            below it stays conditional: that one is the supplier's omission,
            which costs the business the VAT unless it asks for a corrected
            invoice, and it is only worth a line when there is one to ask for.
          */}
          <p className="text-[11px] text-muted-foreground">
            {t('invoices.vatDisclaimer')}
          </p>
          {summary.missingAllocationNumber > 0 && (
            <p className="text-[11px] text-warning">
              {t('invoices.missingAllocation', { count: summary.missingAllocationNumber })}
            </p>
          )}
          {/*
            1.14 — this one speaks about the list the filter is on, so it reads
            the filter's own count: over a selection, `summary` is the ticked
            invoices', and "every invoice in this period has an amount" would
            be said about a period it is not the total of.
          */}
          {missingOnly && filterSummary && (
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-[11px] text-warning">
                {filterSummary.missingAmount > 0
                  ? t('invoices.missingAmount', { count: filterSummary.missingAmount })
                  : t('invoices.missingAmountEmpty')}
              </p>
              <button
                type="button"
                onClick={() => setMissingOnly(false)}
                className="text-[11px] font-semibold text-primary hover:underline"
              >
                {t('invoices.clearFilter')}
              </button>
            </div>
          )}
        </div>
      )}

      {/*
        INV-006 — the money first, the mailbox under it.

        This strip used to open the screen, so the first thing on the page was
        plumbing: which mailbox is connected, and when it last ran. What the
        page is read for is the total and the VAT credit under it, so that card
        takes the top now and the strip sits below it — still above the filters,
        which is where its own two actions belong.

        Worki syncs real mailboxes, so each connected account carries its own
        state and its own two actions. The reference has no equivalent.
      */}
      {connected.length > 0 && (
        <div className="flex items-start gap-2 mb-4">
        <div className="flex-1 min-w-0 space-y-2">
          {connected.map((account) =>
            // Replaced, not decorated: a spinning 16px icon on a row that is
            // otherwise unchanged is what made a running sync look like a
            // click that did nothing. Driven by the server's own job state, so
            // a reload mid-scan comes back to the panel, not to an idle row.
            account.sync?.status === 'running' ? (
              <MailScanPanel key={account.id} email={account.email} state={account.sync} />
            ) : (
              <div key={account.id} className="glass-card list-tint rounded-lg px-3 py-2.5 flex items-center gap-2.5">
                <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                  <Mail className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate ltr-nums" dir="ltr" style={{ textAlign: 'start' }}>
                    {account.email}
                  </p>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {account.lastSyncAt
                      ? `${t('invoices.lastSync')} · ${formatDate(account.lastSyncAt, locale)}`
                      : t('invoices.notSyncedYet')}
                  </p>
                  {/* INV-010 — the way back, in words and on the row itself.
                      Under the address rather than beside the badge, where a
                      phone has no width left, and styled as the connect strip's
                      button is, because it is the one thing this row needs. */}
                  {awaitingLogin(account) && (
                    <button
                      type="button"
                      onClick={() => setReconnectTarget(account)}
                      className="mt-1.5 px-3 py-1 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:opacity-90 transition-opacity active:scale-[0.97]"
                    >
                      {t('invoices.reconnect')}
                    </button>
                  )}
                </div>
                {/* Amber, not red: nothing is lost and nothing is broken on our
                    side. The mailbox is waiting for its owner, who can fix it
                    in a minute from here. */}
                <span
                  className={cn(
                    'px-2 py-0.5 rounded-full text-[10px] font-medium shrink-0',
                    awaitingLogin(account)
                      ? 'bg-warning/10 text-warning'
                      : account.status === 'active'
                        ? 'bg-success/10 text-success'
                        : 'bg-destructive/10 text-destructive',
                  )}
                >
                  {awaitingLogin(account)
                    ? t('invoices.statusReconnect')
                    : account.status === 'active'
                      ? t('invoices.statusActive')
                      : t('invoices.statusError')}
                </span>
                <button
                  type="button"
                  // A scan started against a mailbox whose login lapsed fails
                  // the way the last one did, so the same button asks for the
                  // login instead of spending another scan on finding that out.
                  onClick={() => (awaitingLogin(account) ? setReconnectTarget(account) : sync.mutate({ id: account.id }))}
                  disabled={sync.isPending && sync.variables?.id === account.id}
                  aria-label={t('invoices.syncNow')}
                  title={t('invoices.syncNow')}
                  className="p-1.5 rounded-lg hover:bg-muted transition-colors shrink-0"
                >
                  <RefreshCw
                    className={cn(
                      'h-4 w-4 text-muted-foreground',
                      sync.isPending && sync.variables?.id === account.id && 'animate-spin',
                    )}
                  />
                </button>
                {/* The universal power mark rather than a broken chain link:
                    what this does to a mailbox is switch it off, and that is a
                    symbol every screen in the world already teaches. */}
                <button
                  type="button"
                  onClick={() => setDisconnectTarget(account)}
                  aria-label={t('invoices.disconnect')}
                  title={t('invoices.disconnect')}
                  className="p-1.5 rounded-lg hover:bg-destructive/10 transition-colors shrink-0"
                >
                  <Power className="h-4 w-4 text-muted-foreground" />
                </button>
              </div>
            ),
          )}

        </div>
        {actionsMenu}
        </div>
      )}

      {/* Wraps rather than scrolls: on a phone the three filters and the
          count do not fit on one line, and a row that overflows sideways
          hides the last filter behind an edge nobody thinks to drag. */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {connected.length > 1 && (
          <Select
            value={accountId}
            aria-label={t('invoices.fromAccount')}
            onChange={(value) => setAccountId(value)}
            className="h-9 px-3 bg-muted w-auto shrink-0 max-w-[45%]"
          >
            <option value="">{t('invoices.allAccounts')}</option>
            {connected.map((account) => (
              <option key={account.id} value={account.id}>
                {account.email}
              </option>
            ))}
          </Select>
        )}
        {/*
          INV-006 — a year of mail lands here in one scan, so a period is the
          filter that makes the list usable. Calendar periods, because the
          totals below are read to answer an accounting question and an
          accounting month starts on the first.

          One control rather than the three this used to be: a running period, a
          year, the months inside it, and a span typed by hand are four ways of
          answering the same question, and asking it once is what lets the
          answer be stated in one line instead of spread over a row of boxes.
        */}
        <PeriodPicker value={period} onChange={setPeriod} available={years} earliest={archiveFirstDay} locale={locale} />
        {/*
          INV-006 — the periods above answer "when"; this answers "what for".

          1.10 — "unfiled" used to be a choice of its own here, on the theory
          that it is the list someone works through when they file. Working
          through it is exactly what went wrong: every invoice filed from it
          left the list the moment it was filed, which read as invoices lost
          ("כשמוסיפים חשבוניות לתיקייה הם לא נשארות בליסט"). With no folder
          chosen the list is every invoice, filed or not, and filing one leaves
          it where it was.
        */}
        <FolderFilter
          value={folderId}
          folders={folderList}
          onChange={setFolderId}
          sharing={shareFolderContents.isPending}
          onShare={(folder) => shareFolderContents.mutate([folder])}
          onRename={renameFolder}
          onCreate={createFolder}
          onDelete={setFolderToDelete}
        />
        {/*
          INV-007 — the periods answer "when", the folders "what for", and this
          answers "what kind of document is it". It sits beside them rather
          than under the totals, because it narrows the same list the other two
          do and the summary above reads whatever is filtered.
        */}
        <DocTypeFilter
          value={docTypes}
          onToggle={(option) =>
            setDocTypes((was) => {
              const next = new Set(was);
              if (!next.delete(option)) next.add(option);
              return next;
            })
          }
          onClear={() => setDocTypes(new Set())}
        />
        {/* Switched on from the warning under the totals, and cleared from
            here: a filter with no visible way off is one someone reloads the
            page to escape. */}
        {missingOnly && (
          <button
            type="button"
            onClick={() => setMissingOnly(false)}
            className="h-9 px-3 shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-warning/15 text-warning text-xs font-medium hover:bg-warning/25 transition-colors"
          >
            {t('invoices.missingAmountFilter')}
            <X className="h-3.5 w-3.5" />
          </button>
        )}
        <span className="text-xs text-muted-foreground shrink-0">{t('invoices.count', { count: total })}</span>
        {/*
          Holding a row for half a second opens the checkboxes. A button saying
          so used to stand here and was taken out on the request of the person
          who uses this screen every day: once the hold is known it is a chip
          that never gets pressed, on a filter row that is already full.
        */}
      </div>

      {(selectMode || selection.size > 0) && (
        <SelectionActionBar count={selection.size} keepVisible={selectMode} onClear={() => {
          setSelection(new Set());
          setSelectMode(false);
        }}>
          {/*
            1.10 — the rows on screen, beside every result. "Select all" takes
            the whole filter and a hold takes one row; nothing took the page,
            which is what was asked for ("בחירת העמוד בלבד או סימון הכל").

            It replaces the selection, as "select all" does, so what is selected
            is exactly this page and never this page on top of another. It waits
            while "select all" is being fetched: that answer arrives later and
            would replace the page just chosen.
          */}
          {pageChoiceOffered(rows, total, selection) && (
            <button
              type="button"
              disabled={total === 0 || selectEverything.isPending || invoices.isFetching || search !== debouncedSearch}
              onClick={() => {
                setSelection(new Set(rows.map((row) => row.id)));
                setSelectMode(true);
              }}
              className="text-xs text-primary hover:underline disabled:opacity-40 disabled:no-underline"
            >
              {t('invoices.selectPageOnly', { count: rows.length })}
            </button>
          )}
          {/*
            A month of invoices is the batch an accountant is sent, and tapping
            twenty-five rows to send it is the thing this whole bar exists to
            avoid. It takes everything the filters have narrowed the list to and
            not the page on screen — a folder is longer than a page, and a
            folder is exactly what gets passed on.

            Gone once everything is selected, rather than turning into a "clear
            selection": that stood beside "cancel" at the end of the bar as a
            second way out of the same state, and the person who uses this
            screen every day asked for one. Cancel is the one.
          */}
          {!allSelected && (
            <button
              type="button"
              disabled={total === 0 || selectEverything.isPending || invoices.isFetching || search !== debouncedSearch}
              onClick={() => selectEverything.mutate({ epoch: ++selectionEpoch.current, context: selectionReadContext.current, filters: { ...filters } })}
              className="text-xs text-primary hover:underline disabled:opacity-40 disabled:no-underline"
            >
              {t('invoices.selectAllMatching', { count: total })}
            </button>
          )}
          {/*
            INV-006 — before the delete, and the only one of the two that is not
            destructive. Passing a month on to an accountant is the reason to
            select several invoices at all; deleting them is the rarer thing.
          */}
          <button
            type="button"
            disabled={selection.size === 0}
            onClick={() => {
              if (selection.size > LIMITS.maxBulkInvoices) { toast.error(t('common.bulkShareLimit', { count: LIMITS.maxBulkInvoices })); return; }
              setShareIds([...selection]);
              setShareFolders([]);
              setShareInvoiceName('');
              setShareOpen(true);
            }}
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline disabled:opacity-40 disabled:no-underline"
          >
            <Share2 className="h-3.5 w-3.5" />
            {t('invoices.share')}
          </button>
          {/*
            Filing is the action the folders exist for, so it is offered where
            the selection is, not behind the manage dialog. It reads as a menu
            because "which folder" is the whole question; picking one performs
            the move, and the control returns to its label for the next batch.

            1.13 — "תיקייה חדשה…" is the one choice that is not a folder yet. It
            asks for a name, makes the folder, and only then files the selection
            into it, as picking a folder that was there does. It stands under
            the label rather than after the folders, where a long list would
            hide it.
          */}
          <Select
            value=""
            aria-label={t('invoices.moveToFolder')}
            disabled={selection.size === 0 || move.isPending}
            onChange={(value) => {
              if (value === NEW_FOLDER) setNaming(true);
              else if (value) move.mutate(value);
            }}
            className="h-7 px-2 bg-muted text-xs w-auto disabled:opacity-40"
          >
            <option value="">{t('invoices.moveToFolder')}</option>
            <option value={NEW_FOLDER}>{t('invoices.moveToNewFolder')}</option>
            {folderList.map((folder) => (
              <option key={folder.id} value={folder.id}>
                {folder.name}
              </option>
            ))}
            <option value="none">{t('invoices.noFolder')}</option>
          </Select>
          <InfoTip label={t('common.explain')} className="-mx-1.5">
            {t('invoices.foldersInfo')}
          </InfoTip>
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            disabled={selection.size === 0}
            className="text-xs text-destructive hover:underline disabled:opacity-40 disabled:no-underline"
          >
            {folderId ? t('invoices.removeFromFolder') : t('common.delete')}
          </button>
        </SelectionActionBar>
      )}

      <div className="space-y-2">
        {rows.map((row) => (
          <div
            key={row.id}
            role="button"
            tabIndex={0}
            /*
             * `select-none` and the callout are what let the hold happen at
             * all inside the packaged app: half a second on a row is also the
             * gesture Android's WebView reads as "select this text", and it
             * wins — the handles come up, the row's own timer never gets its
             * press, and holding a row simply does nothing on a phone. A row
             * is a button; there is no text on it anyone means to select.
             */
            /* py-4, not py-3: the row carries two lines of its own — the
               supplier and a meta line of badges — and at the tighter padding
               they sat closer to the rows above and below than to each other,
               which is what reads as a cramped list rather than as a dense
               one. */
            className="glass-card list-tint rounded-lg px-3 py-4 cursor-pointer hover:border-primary/20 transition-colors select-none [-webkit-touch-callout:none]"
            onClick={() => {
              // The hold already chose this row; the click it ends with would
              // otherwise unchoose it before the finger is off the screen.
              if (longPress.consumeClick()) return;
              if (selectMode) toggleSelect(row.id);
              else setPreview(row);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') setPreview(row);
            }}
            {...longPress.handlers(row.id)}
          >
            <div className="flex items-center gap-3">
              {selectMode && (
                <input
                  type="checkbox"
                  checked={selection.has(row.id)}
                  onChange={() => toggleSelect(row.id)}
                  onClick={(event) => event.stopPropagation()}
                  aria-label={t('common.selectRow')}
                  className="rounded border-border h-4 w-4 accent-primary shrink-0"
                />
              )}
              <div className="p-2 rounded-lg bg-muted shrink-0">
                <Mail className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{row.supplier ?? '—'}</p>
                {/*
                  Every row says where its document came from, because a linked
                  row's file is Worki's copy of a page rather than the supplier's
                  own — worth knowing before reading the amount off it. On the
                  meta line rather than beside the supplier: the name is the part
                  worth the width, and the badge keeps its own.

                  Section 5 — a row can carry three badges and a date, which is
                  more than a phone's line holds beside the amount; the date ran
                  on under the figure. So the line wraps, and the date goes under
                  the badges, whenever there is no mailbox address on it: that
                  one already gives up its width as below, and keeps doing so.
                */}
                <div
                  className={cn(
                    'flex items-center gap-x-1.5 mt-0.5 text-xs text-muted-foreground',
                    !(connected.length > 1 && row.accountEmail) && 'flex-wrap gap-y-1',
                  )}
                >
                  <SourceBadge sourceUrl={row.sourceUrl} source={row.source} />
                  <DocTypeBadge docType={row.docType} />
                  {/* Section 5 — a document the business issued, not one it paid. */}
                  {row.direction === 'income' && <Badge tone="success">{t('invoices.income')}</Badge>}
                  {/*
                    The date keeps its width and the address gives up its own.
                    The two used to be a single truncated string, which on a
                    phone meant the mailbox name filled the line and the date —
                    the part a supplier's row is read for — was the half cut
                    off. And which mailbox it came from is only information when
                    there is more than one, the same rule the account filter
                    above is shown under.
                  */}
                  {connected.length > 1 && row.accountEmail && (
                    <span className="truncate min-w-0">{row.accountEmail}</span>
                  )}
                  {connected.length > 1 && row.accountEmail && row.issuedAt && (
                    <span aria-hidden className="shrink-0">
                      ·
                    </span>
                  )}
                  {row.issuedAt && (
                    <span className="shrink-0 ltr-nums">{formatDate(row.issuedAt, locale)}</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {amountOf(row) && <span className="text-sm font-semibold ltr-nums">{amountOf(row)}</span>}
                {selectMode && (
                  <button
                    type="button"
                    aria-label={t('common.view')}
                    title={t('common.view')}
                    onMouseDown={(event) => event.stopPropagation()}
                    onTouchStart={(event) => event.stopPropagation()}
                    onClick={(event) => {
                      event.stopPropagation();
                      setPreview(row);
                    }}
                    className="p-2 rounded-lg hover:bg-muted transition-colors"
                  >
                    <Eye className="h-4 w-4" aria-hidden />
                  </button>
                )}
                {/* Sending one invoice on used to mean holding the row to open
                    selection, ticking it, then finding the share button above
                    the list — three steps that nobody finds by looking. It is
                    the first thing in the row's own menu instead: on a phone
                    the amount, a share icon and the three dots were three
                    targets inside the same thumb's width, and the menu is
                    where every other per-row action on this screen already
                    is. */}
                <span onClick={(event) => event.stopPropagation()}>
                  <ItemActions
                    onView={selection.has(row.id) ? () => setPreview(row) : undefined}
                    onShare={() => {
                      setShareIds([row.id]);
                      setShareFolders([]);
                      setShareInvoiceName(row.supplier ?? '');
                      setShareOpen(true);
                    }}
                    shareLabel={t('invoices.shareRowAction')}
                    onDownload={() => void saveInvoice(row)}
                    deleteLabel={folderId ? t('invoices.removeFromFolder') : undefined}
                    onDelete={() => {
                      setSelection(new Set([row.id]));
                      setConfirmDelete(true);
                    }}
                  />
                </span>
              </div>
            </div>
          </div>
        ))}

        {invoices.isLoading && (
          <div className="text-center py-8 text-sm text-muted-foreground">{t('common.loading')}</div>
        )}
        {folderHasHiddenInvoices && (
          <div role="status" className="glass-card rounded-xl px-4 py-3 text-sm flex flex-wrap items-center justify-between gap-3">
            <p>{t('invoices.folderFiltered', { count: chosenFolder.count, visible: total })}</p>
            <Button variant="secondary" onClick={showAllInFolder}>{t('invoices.showAllInFolder')}</Button>
          </div>
        )}
        {!invoices.isLoading && rows.length === 0 && !folderHasHiddenInvoices && (
          <div className="glass-card rounded-xl text-center py-10 px-4 space-y-2">
            <Mail className="h-8 w-8 mx-auto text-muted-foreground/50" />
            <p className="text-sm font-medium">{t('invoices.emptyTitle')}</p>
            <p className="text-xs text-muted-foreground">{t('invoices.emptyBody')}</p>
          </div>
        )}
      </div>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between gap-3 mt-4">
          <button
            type="button"
            disabled={page === 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            className="h-9 px-3 rounded-lg bg-muted text-sm disabled:opacity-40"
          >
            {t('common.previous')}
          </button>
          <span className="text-xs text-muted-foreground ltr-nums">
            {t('common.page')} {page} {t('common.of')} {pages}
          </span>
          <button
            type="button"
            disabled={page >= pages}
            onClick={() => setPage((current) => current + 1)}
            className="h-9 px-3 rounded-lg bg-muted text-sm disabled:opacity-40"
          >
            {t('common.next')}
          </button>
        </div>
      )}

      <DetailDialog
        open={Boolean(preview)}
        onClose={() => setPreview(null)}
        title={t('invoices.detailTitle')}
        size="wide"
        /* Downstairs, not under the document. The preview has its own scroll
           and holds onto a finger that lands on it, so a button below it was
           one a phone could not reach. */
        footer={
          preview && (
            <>
              <button
                type="button"
                onClick={() => void saveInvoice(preview)}
                className="h-9 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium flex items-center hover:opacity-90 transition-opacity"
              >
                {t('invoices.download')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setSelection(new Set([preview.id]));
                  setPreview(null);
                  setConfirmDelete(true);
                }}
                className="h-9 px-4 rounded-lg bg-destructive/10 text-destructive text-sm font-medium hover:bg-destructive/20 transition-colors"
              >
                {folderId ? t('invoices.removeFromFolder') : t('common.delete')}
              </button>
            </>
          )
        }
      >
        {preview && (
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                {/*
                  Section 5 — on an income document the name is the customer's,
                  the business being the one that issued it; said above the name
                  so it is not read as a supplier's.
                */}
                {preview.direction === 'income' && (
                  <p className="mb-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Badge tone="success">{t('invoices.income')}</Badge>
                    <span>{t('invoices.customer')}</span>
                  </p>
                )}
                <h3 className="text-base font-bold truncate">{preview.supplier ?? '—'}</h3>
                {preview.number && (
                  <p className="text-xs text-muted-foreground ltr-nums">
                    {t('invoices.number')} {preview.number}
                  </p>
                )}
              </div>
              {amountOf(preview) && (
                <span className="text-lg font-bold ltr-nums shrink-0">{amountOf(preview)}</span>
              )}
            </div>

            <dl className="rounded-xl bg-muted/60 divide-y divide-border/60">
              <InvoiceDateRows invoice={preview} locale={locale} />
              {/* A photographed or uploaded invoice came through no mailbox, so
                  there is no account to name and no attachment it came as. */}
              {preview.source !== 'manual' && (
                <DetailRow label={t('invoices.fromAccount')}>
                  <span className="ltr-nums" dir="ltr">
                    {preview.accountEmail ?? '—'}
                  </span>
                </DetailRow>
              )}
              <DetailRow label={t('invoices.source')}>
                {preview.source === 'manual' ? (
                  <span>{t('invoices.sourceBadgeManual')}</span>
                ) : preview.sourceUrl ? (
                  <span className="inline-flex items-center gap-1.5 text-primary">
                    <Link2 className="h-3.5 w-3.5 shrink-0" />
                    {/* The host, not the whole address: a billing URL is mostly
                        token, and the part that identifies it is the site. */}
                    <span dir="ltr" className="truncate">
                      {hostOf(preview.sourceUrl)}
                    </span>
                  </span>
                ) : (
                  <span>{t('invoices.sourceAttachment')}</span>
                )}
              </DetailRow>
            </dl>

            {preview.sourceUrl && (
              <p className="text-xs text-muted-foreground">{t('invoices.fromLinkNote')}</p>
            )}

            <PdfPreview
              src={apiUrl(`/invoices/${preview.id}/download`)}
              fallbackPath={`/invoices/${preview.id}/download?inline=1`}
              fallbackName={`invoice-${preview.id}.pdf`}
            />

            {preview.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {preview.tags.map((tag) => (
                  <span key={tag} className="px-2 py-0.5 rounded-full bg-muted text-[10px] text-muted-foreground">
                    {tag}
                  </span>
                ))}
              </div>
            )}

          </div>
        )}
      </DetailDialog>

      {forwardingOpen && <InvoiceForwardingDialog
        open={forwardingOpen}
        onClose={() => setForwardingOpen(false)}
        folders={folders.data?.items ?? []}
        accounts={connected}
      />}
      <ConnectAccountDialog
        open={connectOpen}
        driver={accounts.data?.driver}
        oauthProviders={accounts.data?.oauthProviders ?? []}
        onClose={() => setConnectOpen(false)}
        onConnected={invalidateAll}
      />

      {/* Mounted only while open, and keyed by the mailbox, so each opening
          starts from an empty field and no earlier refusal. */}
      {reconnectTarget && (
        <ReconnectMailboxDialog
          key={reconnectTarget.id}
          account={reconnectTarget}
          onClose={() => setReconnectTarget(null)}
          onReconnected={reconnected}
        />
      )}

      <ConfirmDialog
        open={disconnectTarget !== null}
        title={t('invoices.disconnect')}
        message={
          // Until the count is back, the warning is stated without it rather
          // than with a zero that would read as "nothing will be lost".
          (disconnectCount.data
            ? t('invoices.disconnectConfirm', {
                email: disconnectTarget?.email ?? '',
                count: disconnectCount.data.total,
              })
            : t('invoices.disconnectConfirmCounting', { email: disconnectTarget?.email ?? '' })) +
          // INV-010 — disconnecting a mailbox that only needed a login is the
          // mistake this whole flow exists to prevent, so the one warning that
          // is read before it happens says there was another way.
          (disconnectTarget && awaitingLogin(disconnectTarget) ? ` ${t('invoices.disconnectInsteadOfReconnect')}` : '')
        }
        confirmLabel={t('invoices.disconnect')}
        loading={disconnect.isPending}
        onCancel={() => setDisconnectTarget(null)}
        onConfirm={() => disconnectTarget && disconnect.mutate(disconnectTarget.id)}
      />

      <ConfirmDialog
        open={confirmReset}
        title={t('invoices.resetAll')}
        message={t('invoices.resetAllConfirm')}
        confirmLabel={t('invoices.resetAllAction')}
        loading={reset.isPending}
        onCancel={() => setConfirmReset(false)}
        onConfirm={() => reset.mutate()}
      />

      <ConfirmDialog
        open={confirmDelete}
        title={folderId ? t('invoices.removeFromFolder') : t('common.delete')}
        confirmLabel={folderId ? t('invoices.removeFromFolder') : t('common.delete')}
        message={
          folderId ? t('invoices.removeFromFolderConfirm') : selection.size > 1
            ? t('invoices.deleteSelectedConfirm', { count: selection.size })
            : t('invoices.deleteConfirm')
        }
        loading={bulkDelete.isPending}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => bulkDelete.mutate()}
      />

      <ManageFoldersDialog
        open={foldersOpen}
        folders={folderList}
        sharing={shareFolderContents.isPending}
        onShare={(chosen) => shareFolderContents.mutate(chosen)}
        onClose={() => setFoldersOpen(false)}
        onChanged={foldersChanged}
      />

      {/* 1.13 — the name asked for when the bar's folder menu chooses a new
          folder. Mounted only while open, so each opening starts from an empty
          field and no earlier refusal. Once the folder is made, the selection
          is filed into it by the move every other choice in that menu makes. */}
      {naming && (
        <NewFolderDialog
          onClose={() => setNaming(false)}
          onCreate={createFolder}
          onCreated={(folder) => {
            setNaming(false);
            move.mutate(folder.id);
          }}
        />
      )}

      {/* 10.1 — asked before a folder goes from the folder menu: by name, since
          the bin is pressed in a list of them, and saying what becomes of the
          invoices inside, since a bin over a folder of forty documents reads
          like a threat to the documents. */}
      <ConfirmDialog
        open={folderToDelete !== null}
        title={t('invoices.deleteFolder')}
        message={t('invoices.deleteFolderNamed', {
          name: folderToDelete?.name ?? '',
          count: folderToDelete?.count ?? 0,
        })}
        loading={removeFolder.isPending}
        onCancel={() => setFolderToDelete(null)}
        onConfirm={() => folderToDelete && removeFolder.mutate(folderToDelete)}
      />

      {/* Section 3 — every file an invoice of its own, read and checked on one
          screen, and filed a row each. "שמירה ושליחה" goes straight on to the
          share of what was saved: the folder it went in, where it went in one. */}
      <InvoiceScanDialog
        initialFolderId={folderId}
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        camera={camera}
        onSaved={(result, then) => {
          invalidateAll();
          if (then !== 'share') return;
          setShareIds(result.ids);
          setShareFolders(result.folder ? [{ ...result.folder, count: result.ids.length }] : []);
          setShareInvoiceName('');
          setShareOpen(true);
        }}
      />

      <ShareInvoicesDialog
        open={shareOpen}
        ids={shareIds}
        folders={shareFolders}
        invoiceName={shareInvoiceName}
        onClose={() => setShareOpen(false)}
        onShared={() => {
          setSelection(new Set());
          setSelectMode(false);
          // 1.8 — a folder that was just sent says so on its line.
          void queryClient.invalidateQueries({ queryKey: ['invoice-folders'] });
        }}
      />
    </div>
  );
}

/**
 * INV-006 — making, renaming and removing folders.
 *
 * Deliberately not a screen of its own: a folder is worth about as much typing
 * as its name, and the filing itself happens on the list behind this dialog.
 *
 * Deleting one unfiles what was in it and deletes no invoice, which the
 * confirmation says outright — the word "delete" over a folder holding forty
 * documents reads like a threat to the documents.
 */
function ManageFoldersDialog({
  open,
  folders,
  sharing,
  onShare,
  onClose,
  onChanged,
}: {
  open: boolean;
  folders: InvoiceFolder[];
  /** A folder's contents are being fetched, so no row offers a second press. */
  sharing: boolean;
  /** Hand these whole folders to someone — see the mutation behind it. */
  onShare: (folders: InvoiceFolder[]) => void;
  onClose: () => void;
  /** Called after any change; carries the id when the change was a deletion. */
  onChanged: (deletedId?: string) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [name, setName] = useState('');
  const [editing, setEditing] = useState<InvoiceFolder | null>(null);
  const [confirming, setConfirming] = useState<InvoiceFolder | null>(null);
  const [emptying, setEmptying] = useState<InvoiceFolder | null>(null);
  const [error, setError] = useState<string | null>(null);
  /**
   * Folders ticked to go out together — a quarter is three or four of these,
   * and sending them one at a time makes the accountant four documents where
   * they wanted one.
   */
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const chosen = folders.filter((folder) => picked.has(folder.id));

  // A folder deleted or renamed behind this leaves an id ticked that no longer
  // names anything, so the ticks start clean each time the dialog opens.
  //
  // 1.10 — and a name being typed over when the dialog closes is dropped, so
  // the folder keeps its old name. Closing takes the field away with the rest
  // of the dialog, which is not always reported as leaving it, so the closing
  // itself drops the name.
  useEffect(() => {
    setEditing(null);
    if (open) setPicked(new Set());
  }, [open]);

  const run = <T,>(action: () => Promise<T>, after: () => void) => {
    setError(null);
    action()
      .then(() => after())
      .catch((failure: unknown) => setError(failure instanceof RequestError ? failure.code : 'errors.unknown'));
  };

  const create = () => {
    const parsed = invoiceFolderSchema.safeParse({ name });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'errors.validation');
      return;
    }
    run(
      () => api.post<InvoiceFolder>('/invoices/folders', parsed.data),
      () => {
        setName('');
        onChanged();
      },
    );
  };

  /*
   * 1.10 — a name typed over a folder here is kept by Enter or the tick beside
   * its field, and by nothing else, as in the folder menu. Leaving the field
   * used to keep it, so a name started and then abandoned — another row
   * pressed, the dialog closed — became the folder's name. Leaving the field,
   * Escape and closing the dialog now all put the old name back.
   */
  const endEdit = (folder: InvoiceFolder, keep: boolean) => {
    // Only this folder's rename ends: a field taken away late must not end the
    // rename of the next folder pressed.
    setEditing((was) => (was?.id === folder.id ? null : was));
    if (!keep || editing?.id !== folder.id) return;
    const trimmed = editing.name.trim();
    // A name typed back exactly as it was is not a rename, and sending it would
    // only fail on its own uniqueness check. An emptied one is never sent.
    if (!trimmed || trimmed === folder.name) return;
    run(
      () => api.put<InvoiceFolder>(`/invoices/folders/${folder.id}`, { name: trimmed }),
      onChanged,
    );
  };

  return (
    <>
      <Modal
        open={open}
        title={t('invoices.manageFolders')}
        size="wide"
        onClose={onClose}
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>
              {t('common.close')}
            </Button>
            {/* Only there once something is ticked: an always-present button
                that does nothing most of the time is a button people learn to
                ignore. */}
            {chosen.length > 0 && (
              <Button variant="primary" loading={sharing} onClick={() => onShare(chosen)}>
                {t('invoices.shareFolders', { count: chosen.length })}
              </Button>
            )}
          </>
        }
      >
        <div className="space-y-3">
          {error && <ErrorNotice code={error} />}

          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Field label={t('invoices.newFolder')} htmlFor="folder-name">
              <Input
                id="folder-name"
                value={name}
                placeholder={t('invoices.folderName')}
                onChange={(event) => setName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    create();
                  }
                }}
                />
              </Field>
            </div>
            <Button variant="primary" onClick={create}>
              {t('common.add')}
            </Button>
          </div>

          <div className="space-y-2">
            {folders.length === 0 && <p className="text-sm text-muted-foreground">{t('common.empty')}</p>}
            {folders.map((folder) => (
              <div key={folder.id} className="flex items-center gap-2 rounded-lg bg-muted/40 px-3 py-2">
                {/* Ticking several sends them as one document. Disabled on an
                    empty folder for the same reason the share icon is: there is
                    nothing in it to send. */}
                <input
                  type="checkbox"
                  className="h-4 w-4 shrink-0 accent-primary disabled:opacity-30"
                  checked={picked.has(folder.id)}
                  disabled={folder.count === 0}
                  aria-label={folder.name}
                  onChange={() =>
                    setPicked((was) => {
                      const next = new Set(was);
                      if (!next.delete(folder.id)) next.add(folder.id);
                      return next;
                    })
                  }
                />
                {editing?.id === folder.id ? (
                  /* 1.10 — the name, typed over where it stands, with a tick
                     beside it to keep it. */
                  <>
                    <Input
                      autoFocus
                      value={editing.name}
                      enterKeyHint="done"
                      aria-label={t('invoices.folderName')}
                      onChange={(event) => setEditing({ ...editing, name: event.target.value })}
                      onBlur={() => endEdit(folder, false)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') endEdit(folder, true);
                        if (event.key === 'Escape') endEdit(folder, false);
                      }}
                      className="flex-1 min-w-0"
                    />
                    <button
                      type="button"
                      // Pressing the tick would take the focus from the field
                      // before the tick is clicked, and leaving the field puts
                      // the old name back.
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => endEdit(folder, true)}
                      aria-label={t('common.save')}
                      title={t('common.save')}
                      className="shrink-0 text-primary hover:opacity-70 transition-opacity"
                    >
                      <Check className="h-4 w-4" />
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setEditing(folder)}
                    className="flex-1 text-start text-sm hover:underline"
                  >
                    {folder.name}
                  </button>
                )}
                <span className="text-xs text-muted-foreground shrink-0">
                  {t('invoices.invoiceCount', { count: folder.count })}
                </span>
                {/*
                  Sending the folder on is what a folder is for, and it stands
                  first of the three because it is the one that happens weekly
                  — the other two are housekeeping. Nothing to send from an
                  empty folder, so it is unavailable rather than silent.
                */}
                <button
                  type="button"
                  disabled={folder.count === 0 || sharing}
                  onClick={() => onShare([folder])}
                  aria-label={t('invoices.shareFolderAction')}
                  title={t('invoices.shareFolderAction')}
                  className="text-primary hover:opacity-70 transition-opacity disabled:opacity-30 disabled:hover:opacity-30"
                >
                  <Share2 className="h-4 w-4" />
                </button>
                {/*
                  INV-006 — emptying is not deleting, and it sits next to the
                  bin so the difference is visible at the moment of choosing:
                  this takes the invoices out and keeps them, the bin next to it
                  removes the folder. Nothing to do on an empty folder, so it
                  says so by being unavailable rather than by doing nothing.
                */}
                <button
                  type="button"
                  disabled={folder.count === 0}
                  onClick={() => setEmptying(folder)}
                  aria-label={t('invoices.emptyFolder')}
                  title={t('invoices.emptyFolder')}
                  className="text-muted-foreground hover:text-foreground transition-colors disabled:opacity-30 disabled:hover:text-muted-foreground"
                >
                  <FolderMinus className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setConfirming(folder)}
                  aria-label={t('invoices.deleteFolder')}
                  className="text-destructive hover:opacity-70 transition-opacity"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={emptying !== null}
        title={t('invoices.emptyFolder')}
        message={t('invoices.emptyFolderConfirm', { count: emptying?.count ?? 0 })}
        confirmLabel={t('invoices.emptyFolderAction')}
        onCancel={() => setEmptying(null)}
        onConfirm={() => {
          const folder = emptying;
          if (!folder) return;
          setEmptying(null);
          run(
            () => api.post<{ moved: number }>(`/invoices/folders/${folder.id}/empty`, {}),
            () => {
              onChanged();
              toast.success(t('invoices.emptied', { count: folder.count }));
            },
          );
        }}
      />

      <ConfirmDialog
        open={confirming !== null}
        title={t('invoices.deleteFolder')}
        message={t('invoices.deleteFolderConfirm')}
        onCancel={() => setConfirming(null)}
        onConfirm={() => {
          const folder = confirming;
          if (!folder) return;
          setConfirming(null);
          run(
            () => api.del(`/invoices/folders/${folder.id}`),
            () => {
              onChanged(folder.id);
              toast.success(t('common.deleted'));
            },
          );
        }}
      />
    </>
  );
}

/**
 * 1.13 — the name for a folder made from the selection bar, asked for before
 * anything is made or moved.
 *
 * Nitzan: "כשאני מבצע בחירה מרובה של חשבוניות לתת את האפשרות להוסיף אותם לתיקייה
 * חדשה לא רק לתיקייה קיימת". The bar listed the folders there already were, so a
 * batch for one that was not there yet meant leaving the selection, making the
 * folder in the manage dialog, and selecting the same invoices all over again.
 *
 * It asks as the manage dialog asks for a new folder, in a dialog of the page's
 * own rather than through `window.prompt`, a bare browser box that does not look
 * like the app. The name is checked as every other new-folder field checks one.
 * A name the server refuses, such as one another folder already has, is shown
 * here with the field left as it was, and nothing is moved.
 *
 * Making a folder and filing into it are two requests. If the second fails after
 * the first went through, the folder stays and the selection is still selected,
 * so the same menu files it into the folder that is now on it.
 */
function NewFolderDialog({
  onClose,
  onCreate,
  onCreated,
}: {
  onClose: () => void;
  /** Settles with the folder made; rejects with the server's refusal. */
  onCreate: (name: string) => Promise<InvoiceFolder>;
  /** The folder is there, so the selection is filed into it from here. */
  onCreated: (folder: InvoiceFolder) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** Whether the name has been sent and the folder is not yet made. */
  const [making, setMaking] = useState(false);

  // Leaving once the folder is on its way would still file the selection into
  // it, which is not what leaving means, so the way out waits for the answer.
  const dismiss = () => {
    if (!making) onClose();
  };

  const create = () => {
    if (making) return;
    const parsed = invoiceFolderSchema.safeParse({ name });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'errors.validation');
      return;
    }
    setError(null);
    setMaking(true);
    void onCreate(parsed.data.name)
      .then(onCreated)
      .catch((failure: unknown) => setError(failure instanceof RequestError ? failure.code : 'errors.unknown'))
      .finally(() => setMaking(false));
  };

  return (
    <Modal
      open
      title={t('invoices.newFolder')}
      onClose={dismiss}
      footer={
        <>
          <Button variant="ghost" onClick={dismiss}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" loading={making} onClick={create}>
            {t('invoices.createAndMove')}
          </Button>
        </>
      }
    >
      {error && <ErrorNotice code={error} />}
      <Field label={t('invoices.folderName')} htmlFor="selection-folder-name">
        <Input
          id="selection-folder-name"
          value={name}
          autoFocus
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              create();
            }
          }}
        />
      </Field>
    </Modal>
  );
}

interface ShareResult {
  failures?: Array<'email' | 'whatsapp'>;
  /** Per channel, present only for the ones that were asked for. */
  email?: { delivered: boolean; attached: boolean };
  whatsapp?: { delivered: boolean; deepLink: string };
  count: number;
  /** Ids the server could not put in the document — see the service comment. */
  skipped: string[];
  link: string;
}

/** The two ways a batch leaves, in the order the dialog offers them. */
const SHARE_WAYS = [
  { channel: 'email' as const, label: 'quotes.email', icon: Mail },
  { channel: 'whatsapp' as const, label: 'quotes.whatsapp', icon: MessageCircle },
];

/**
 * 1.9 — what sharing these invoices would do, asked for before it is done:
 * `POST /invoices/bulk/share/preview`.
 */
interface SharePreview {
  count?: number;
  skipped?: string[];
  /** What they add up to — the answer the bar over the list gets for the same ids. */
  summary: InvoiceSummary;
  /** Whether the merged PDF is small enough to go as a mail attachment. */
  pdfFits: boolean;
}

/** 1.9 — what the mail carries: the merged PDF itself, or a link to it. */
type ShareDelivery = NonNullable<InvoiceShareInput['delivery']>;

/** The two things a mail can carry, in the order the dialog offers them. */
const SHARE_DELIVERY_WAYS = [
  { delivery: 'pdf' as const, label: 'invoices.shareAsPdf', icon: Paperclip },
  { delivery: 'link' as const, label: 'invoices.shareAsLink', icon: Link2 },
];

/**
 * 1.9 — what the mail will carry: the way the sender picked, else the way the
 * server would go on its own.
 *
 * That is the PDF, or the link when the files are too big to attach. A PDF that
 * does not fit is no choice at all, so one picked before the answer said so
 * gives way to the link rather than being sent and turned into one afterwards.
 */
function shareDeliveryOf(picked: ShareDelivery | null, pdfFits: boolean): ShareDelivery {
  return pdfFits ? (picked ?? 'pdf') : 'link';
}

/**
 * 1.9 — what the invoices being sent add up to, in both ways of sending.
 *
 * The figures are the summary card's and read the same way: the total and the
 * VAT credit at the rate it was worked out at, per currency, then the invoices
 * whose amount was never read — a total that is short has to say so — and the
 * disclaimer the credit is never shown without. Nothing is drawn when there is
 * nothing to add up.
 *
 * Section 5 — income documents in the selection are added up apart, in green,
 * with the output VAT they carry, as on the summary card: the expense figures
 * above them stay the expenses' alone.
 */
function ShareTotals({ summary }: { summary: InvoiceSummary | undefined }) {
  const { t } = useTranslation();
  const locale = currentLocale();
  if (
    !summary ||
    (summary.byCurrency.length === 0 && summary.income.length === 0 && summary.missingAmount === 0)
  ) {
    return null;
  }

  return (
    <div className="rounded-xl border border-primary/20 bg-primary/5 p-3 space-y-2">
      
          {summary.byCurrency.map((entry) => (
        <div key={entry.currency} className="space-y-0.5">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-xs text-muted-foreground">{t('invoices.totalSpent')} · {t('invoices.invoiceCount',{count:entry.count})}</span>
            <span className="inline-flex items-baseline gap-1">
              <span className="text-base font-semibold ltr-nums">{money(entry.total, entry.currency, locale)}</span>
              <InfoTip label={t(`currencies.${entry.currency}`)}>
                  {t('currencies.description', { code: entry.currency, name: t(`currencies.${entry.currency}`) })}
                </InfoTip>
            </span>
          </div>
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-xs text-muted-foreground">
              {t('invoices.vatCredit')}:
              {/* Not `ltr-nums`, which the amounts carry: this is a sentence, and it would read backwards. */}
              <span className="ms-1.5 text-[11px]">{t('invoices.vatCreditHint', { rate: summary.vatRate })}</span>
            </span>
            <span className="text-sm font-medium text-primary ltr-nums">{money(entry.vat, entry.currency, locale)}</span>
          </div>
        </div>
      ))}
      {summary.income.map((entry) => (
        <div key={`income-${entry.currency}`} className="space-y-0.5">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-xs text-success">{t('invoices.incomeTotal')} · {t('invoices.invoiceCount', { count: entry.count })}</span>
            <span className="inline-flex items-baseline gap-1">
              <span className="text-base font-semibold text-success ltr-nums">{money(entry.total, entry.currency, locale)}</span>
              <InfoTip label={t(`currencies.${entry.currency}`)}>{t('currencies.description', { code: entry.currency, name: t(`currencies.${entry.currency}`) })}</InfoTip>
            </span>
          </div>
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-xs text-success">
              {t('invoices.incomeVat')}
              <span className="ms-1.5 text-[11px]">{t('invoices.vatCreditHint', { rate: summary.vatRate })}</span>
            </span>
            <span className="text-sm font-medium text-success ltr-nums">{money(entry.vat, entry.currency, locale)}</span>
          </div>
        </div>
      ))}
      {summary.missingAmount > 0 && (
        <p className="text-[11px] text-warning">{t('invoices.missingAmount', { count: summary.missingAmount })}</p>
      )}
      {(summary.byCurrency.length > 0 || summary.income.length > 0) && (
        <p className="text-[11px] text-muted-foreground">
            {t('invoices.vatDisclaimer')}
          </p>
      )}
    </div>
  );
}

/**
 * 1.9 — PDF or a link, chosen before sending: what the mail carries.
 *
 * Both in front of the eye, for the reason the channels are. They are one or
 * the other, so they are a radio group and not toggles like the channels. The
 * PDF is greyed out, with the reason under it, when the merged file is over
 * what a mail can carry.
 */
function ShareDeliveryChoice({
  value,
  pdfFits,
  onChange,
}: {
  value: ShareDelivery;
  pdfFits: boolean;
  onChange: (next: ShareDelivery) => void;
}) {
  const { t } = useTranslation();
  return (
    <Field label={t('invoices.shareDelivery')} {...(pdfFits ? {} : { hint: t('invoices.sharePdfTooBig') })}>
      <div role="radiogroup" aria-label={t('invoices.shareDelivery')} className="flex gap-2">
        {SHARE_DELIVERY_WAYS.map(({ delivery, label, icon: Icon }) => {
          const on = value === delivery;
          return (
            <button
              key={delivery}
              type="button"
              role="radio"
              aria-checked={on}
              disabled={delivery === 'pdf' && !pdfFits}
              onClick={() => onChange(delivery)}
              className={cn(
                'flex-1 h-11 inline-flex items-center justify-center gap-2 rounded-xl border text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                on
                  ? 'border-primary bg-primary/15 text-primary'
                  : 'border-border bg-muted/40 text-muted-foreground hover:text-foreground',
              )}
            >
              <Icon className="h-4 w-4" />
              {t(label)}
              {on && <Check className="h-3.5 w-3.5" />}
            </button>
          );
        })}
      </div>
    </Field>
  );
}

/**
 * INV-006 — hand a selection of invoices to someone as one document.
 *
 * The two channels differ in what actually travels, and the dialog does not
 * pretend otherwise: mail carries the merged PDF as an attachment, WhatsApp
 * carries a link to it, because a deep link cannot carry a file. The number is
 * optional for WhatsApp — left blank it opens on the contact picker, which is
 * how this reaches a bookkeeper who is in the phone and not in Worki.
 *
 * Both can be on at once, and they are shown as two buttons rather than as a
 * dropdown: they were an either/or in a select, which is wrong twice over — the
 * accountant and the bookkeeper both get this batch, and a way of sending that
 * has to be opened to be seen is one people do not know is there.
 *
 * 1.9 — what the mail carries is the sender's to choose, PDF or link, before it
 * goes. The choice starts where the server would have gone on its own, which
 * the server says for this very set, and the total of the invoices is on show
 * whichever is picked.
 */
function ShareInvoicesDialog({
  open,
  ids,
  folders,
  invoiceName,
  onClose,
  onShared,
}: {
  open: boolean;
  ids: string[];
  /**
   * The folders being handed over whole, when that is what this is. They name
   * the dialog and fill in the file title, because "40 invoices" gives the
   * sender nothing to check against the folders they meant to send.
   */
  folders?: InvoiceFolder[];
  /** The supplier, when a single invoice is being sent from its own row. */
  invoiceName?: string;
  onClose: () => void;
  onShared: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const form = useForm(invoiceShareSchema, { ids, channels: ['email'], to: '', phone: '', title: '' });
  const channels = (form.values.channels as Array<'email' | 'whatsapp'>) ?? [];
  const folderNames = (folders ?? []).map((entry) => entry.name).join(', ');
  /** 1.8 — which folders these are, for the server to write down that they went. */
  const folderIds = (folders ?? []).map((entry) => entry.id);
  /** Whatever this send is of — one supplier, or the folders — under one name. */
  const subject = invoiceName || folderNames;

  /*
   * 1.9 — what sharing these would send, asked for while the dialog is open: what
   * they come to, and whether the merged PDF is small enough for a mail. The key
   * sits under `invoices`, so whatever refreshes the list refreshes this too, and
   * the ids go in sorted: the same ticks in another order are the same question.
   * The dialog is mounted all the time, so it asks only when it is open.
   */
  const sortedIds = [...ids].sort();
  const preview = useQuery({
    queryKey: ['invoices', 'share-preview', sortedIds],
    queryFn: () => api.post<SharePreview>('/invoices/bulk/share/preview', { ids: sortedIds }),
    enabled: open && ids.length > 0,
  });
  /*
   * The way the sender picked, if they did. Until they do, it is where the server
   * would go on its own. While the answer is on its way the PDF is taken to fit:
   * the server turns it into a link if it does not, so nothing is lost by asking
   * a moment early.
   */
  const [picked, setPicked] = useState<ShareDelivery | null>(null);
  const [shareStep,setShareStep]=useState(0);
  const pdfFits = preview.data?.pdfFits ?? true;
  const delivery = shareDeliveryOf(picked, pdfFits);

  // The selection is the dialog's subject, and it is chosen behind the dialog:
  // opening on a stale set would send whatever was selected last time.
  useEffect(() => {
    if (!open) return;
    // Nor is a choice made for last time's batch this one's: each starts where the server would.
    setPicked(null);
    setShareStep(0);
    form.setValue('ids', ids);
    // The addresses are worth keeping — the same bookkeeper every month — but
    // the title is not: it names a particular batch, and last month's name on
    // this month's invoices is a mislabelled file, not a saved keystroke. When
    // folders are being sent, their names are the title worth having.
    form.setValue('title', subject.slice(0, 80));
    // Re-syncing on every render of the parent would fight the user's typing.
  }, [open, ids.join(','), subject]);

  const toggle = (channel: 'email' | 'whatsapp') => {
    const next = channels.includes(channel) ? channels.filter((one) => one !== channel) : [...channels, channel];
    form.setValue('channels', next);
  };

  const share = useMutation({
    mutationFn: (payload: InvoiceShareInput) => api.post<ShareResult>('/invoices/bulk/share', payload),
    onSuccess: (result, sent) => {
      onShared();

      // With the `link` WhatsApp driver nothing left the server; the user
      // finishes the send from their own WhatsApp, so open it for them.
      if (result.whatsapp && !result.whatsapp.delivered) {
        void platform.openExternal(result.whatsapp.deepLink);
      }
      if (result.failures?.length) {
        form.setValue('channels', result.failures);
        form.setFormError('invoices.sharePartial');
        return;
      }
      onClose();

      if (result.email?.attached === false && sent.delivery !== 'link') {
        // The mail went; the file did not fit in it. Said plainly rather than
        // under a green "sent", because the accountant will get a link to open
        // and not a document to save, and the sender is the one who has to know.
        // A link the sender chose is no news to them, so only the fallback says it.
        toast.show(t('invoices.shareSentLink'));
      } else if (result.email) {
        toast.success(t('invoices.shareSent'));
      } else {
        toast.show(t('invoices.shareReady'));
      }

      // Said after the success, not instead of it: the document did go, and it
      // is short. Nothing else on the screen would ever mention it.
      if (result.skipped.length > 0) {
        toast.show(t('invoices.shareSkipped', { count: result.skipped.length }));
      }
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    void form.submit(async (data) => {
      if(folderIds.length>0&&shareStep===0){setShareStep(1);return;}
      // No folders for a selection of invoices: the request is what it was.
      await share.mutateAsync({ ...data, locale: currentLocale(), delivery, folderIds: folderIds.length > 0 ? folderIds : undefined });
    });
  };

  return (
    <Modal
      open={open}
      /* The folder is the whole point of the send, so it is in the heading and
         not only in a count: the sender picked one of several and the only way
         to check they picked the right one is to see its name here. */
      title={
        invoiceName
          ? t('invoices.shareInvoiceTitle', { name: invoiceName })
          : folderNames
            ? t('invoices.shareFolderTitle', { name: folderNames })
            : t('invoices.shareTitle')
      }
      size="wide"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          {folderIds.length>0&&shareStep===1&&<Button onClick={()=>setShareStep(0)}>{t('common.back')}</Button>}
          <Button variant="primary" loading={form.submitting} onClick={submit}>
            {t(folderIds.length>0&&shareStep===0?'common.next':'common.send')}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-3" noValidate>
        {folderIds.length>0&&<p className="text-xs text-muted-foreground"><span dir="ltr">{shareStep+1} / 2</span> · {t(shareStep===0?'quotes.sendVia':'signing.sendFormat')}</p>}
        {form.formError && <ErrorNotice code={form.formError} />}

        <p className="text-xs text-muted-foreground">
          {t('invoices.invoiceCount', { count: preview.data?.count ?? ids.length })}
          {folderNames && !invoiceName && (
            <InfoTip label={t('common.explain')}>{t('invoices.shareFolderInfo')}</InfoTip>
          )}
        </p>

        {/* What the sender calls this batch. It becomes the attachment's name,
            the name behind the link and the head of the subject line — because
            "invoices-40.pdf", arriving from a business the bookkeeper has fifty
            of, says nothing about which forty these are. Optional: leave it and
            the count names the file as it always did. */}
        <div hidden={folderIds.length>0&&shareStep===0}><Field
          label={t('invoices.shareFileTitle')}
          htmlFor="share-file-title"
          {...(form.errors.title ? { error: t(form.errors.title) } : {})}
        >
          <Input
            id="share-file-title"
            value={String(form.values.title ?? '')}
            invalid={Boolean(form.errors.title)}
            onChange={(event) => form.setValue('title', event.target.value)}
          />
        </Field>

        {/* 1.9 — what they come to, VAT included, whether they go as a PDF or as
            a link, and on WhatsApp, which is always a link. */}
        <ShareTotals summary={preview.data?.summary} />

        {Boolean(preview.data?.skipped?.length) && <p role="status" className="text-xs text-warning">{t('invoices.shareSkipped', {count: preview.data?.skipped?.length})}</p>}
        </div>

        {/* Both ways in front of the eye, and both selectable. A dropdown made
            the choice exclusive and hid it: the same batch goes to the
            accountant by mail and to the bookkeeper on WhatsApp, and nobody
            opens a select to find out what a screen can do. */}
        <div hidden={folderIds.length>0&&shareStep===1}><Field
          label={t('quotes.sendVia')}
          {...(form.errors.channels ? { error: t(form.errors.channels) } : {})}
        >
          <div className="flex gap-2">
            {SHARE_WAYS.map(({ channel, label, icon: Icon }) => {
              const on = channels.includes(channel);
              return (
                <button
                  key={channel}
                  type="button"
                  aria-pressed={on}
                  onClick={() => toggle(channel)}
                  className={cn(
                    'flex-1 h-11 inline-flex items-center justify-center gap-2 rounded-xl border text-sm font-medium transition-colors',
                    on
                      ? 'border-primary bg-primary/15 text-primary'
                      : 'border-border bg-muted/40 text-muted-foreground hover:text-foreground',
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {t(label)}
                  {on && <Check className="h-3.5 w-3.5" />}
                </button>
              );
            })}
          </div>
        </Field>

        <div className={cn('grid grid-cols-1 gap-3', channels.length > 1 && 'md:grid-cols-2')}>
          {channels.includes('email') && <Field
            label={t('customer.email')}
            htmlFor="share-to"
            {...(form.errors.to ? { error: t(form.errors.to) } : {})}
          >
            <Input
              id="share-to"
              dir="ltr"
              value={String(form.values.to ?? '')}
              invalid={Boolean(form.errors.to)}
              onChange={(event) => form.setValue('to', event.target.value)}
            />
          </Field>}

        {/* Blank is a real answer: WhatsApp then opens on the contact picker,
            which is how this reaches someone who is in the phone, not in Worki. */}
        {channels.includes('whatsapp') && (
          <Field
            label={t('customer.phone')}
            htmlFor="share-phone"
            hint={t('invoices.sharePhoneHint')}
            {...(form.errors.phone ? { error: t(form.errors.phone) } : {})}
          >
            <Input
              id="share-phone"
              type="tel"
              dir="ltr"
              value={String(form.values.phone ?? '')}
              invalid={Boolean(form.errors.phone)}
              onChange={(event) => form.setValue('phone', event.target.value)}
            />
          </Field>
        )}
        </div>
        </div>

        {/* The delivery format applies to email, separately from recipient details. */}
        {channels.includes('email') && (folderIds.length===0||shareStep===1) && <ShareDeliveryChoice value={delivery} pdfFits={pdfFits} onChange={setPicked} />}
      </form>
    </Modal>
  );
}

/**
 * Where a row's document came from, as a pill.
 *
 * Two states rather than one marker on the unusual case: a badge that appears
 * only on linked rows reads as a warning on them, when what it actually says is
 * which of two ordinary routes the file took.
 */
function SourceBadge({ sourceUrl, source }: { sourceUrl: string | null; source: InvoiceSource }) {
  const { t } = useTranslation();
  /*
   * A hand-added invoice outranks the other two: its amount was typed by a
   * person rather than read off the document, and a row whose number came from
   * somewhere other than the file is worth saying so on.
   */
  if (source === 'manual') {
    return (
      <Badge tone="warning">
        <Camera className="h-3 w-3 shrink-0" aria-hidden />
        {t('invoices.sourceBadgeManual')}
      </Badge>
    );
  }
  return sourceUrl ? (
    <Badge tone="primary">
      <Link2 className="h-3 w-3 shrink-0" aria-hidden />
      {t('invoices.sourceBadgeLink')}
    </Badge>
  ) : (
    <Badge tone="neutral">
      <Paperclip className="h-3 w-3 shrink-0" aria-hidden />
      {t('invoices.sourceBadgeFile')}
    </Badge>
  );
}

/**
 * INV-007 — what the document is, in the one word that decides whether its VAT
 * can be reclaimed.
 *
 * Shown only where it changes the reading. A `חשבונית מס` is what an invoice
 * row is assumed to be, so labelling the ordinary case would put a badge on
 * almost every row and teach the eye to skip all of them; what earns the width
 * is a document that is *not* one, because that row is in the archive and out
 * of the VAT credit, and nothing else on the line explains why.
 */
/**
 * INV-007 — the three types a business can reclaim input VAT against, which is
 * the split the badges draw.
 */
const DEDUCTIBLE_DOC_TYPES: readonly InvoiceDocType[] = [
  'tax_invoice',
  'tax_invoice_receipt',
  'en_tax_invoice',
];

/**
 * 1.10 — what the folder menu files a refused new folder under, in the one slot
 * it keeps for a refusal. No folder's id is this.
 *
 * 1.13 — and what the selection bar's folder menu hands over for its "תיקייה
 * חדשה…" choice, among the ids of folders and "none", so it must not be one.
 */
const NEW_FOLDER = 'new';

/**
 * INV-006 — the folder filter: a drawn menu, not a `<select>`.
 *
 * A native `<option>` can hold text and nothing else, and the thing most often
 * wanted of a folder is not to filter the list down to it but to send it on to
 * whoever files it. So the list is drawn, and every folder carries the share
 * icon beside its name — the same one the manage dialog offers, one press away
 * from the row where the folder is already being looked at.
 *
 * 1.3 — and a pencil, which renames the folder where it is listed. Renaming was
 * offered only inside the manage dialog, by pressing a name that does not look
 * pressable, and nobody found it there.
 *
 * 1.10 — the row above the folders is "תיקיות", and pressing it takes the
 * choice away, so the list is every invoice again. A plus beside it makes a
 * folder in the menu itself. The unfiled pile is no longer offered: every
 * invoice filed from it left the list the moment it was filed.
 *
 * 10.1 — and a bin beside the share icon. The page asks before the folder goes.
 */
function FolderFilter({
  value,
  folders,
  onChange,
  onShare,
  onRename,
  onCreate,
  onDelete,
  sharing,
}: {
  /**
   * A folder id, or empty for every invoice. Anything that names no folder on
   * the list reads as no folder chosen.
   */
  value: string;
  folders: InvoiceFolder[];
  onChange: (id: string) => void;
  onShare: (folder: InvoiceFolder) => void;
  /** Settles once the folders are read again; rejects with the server's refusal. */
  onRename: (folder: InvoiceFolder, name: string) => Promise<void>;
  /** Settles once the folders are read again; rejects with the server's refusal. */
  onCreate: (name: string) => Promise<unknown>;
  /** Asks whether to delete the folder; nothing goes until that is agreed to. */
  onDelete: (folder: InvoiceFolder) => void;
  sharing: boolean;
}) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLButtonElement>(null);

  /** The folder whose name is being typed over, and what is typed so far. */
  const [renaming, setRenaming] = useState<{ folder: InvoiceFolder; name: string } | null>(null);
  /** A new name sent and not yet read back, kept on its row meanwhile. */
  const [sent, setSent] = useState<{ id: string; name: string } | null>(null);
  /**
   * The server's refusal of a rename, shown under the folder it refused, or of
   * a new folder, shown under its field.
   */
  const [refused, setRefused] = useState<{ id: string; code: string } | null>(null);
  /** 1.10 — the name typed for a new folder, while the plus has its field open. */
  const [creating, setCreating] = useState<string | null>(null);
  /** Whether a new folder has been sent and not yet read back. */
  const [making, setMaking] = useState(false);
  const field = useRef<HTMLInputElement>(null);
  /*
   * A rename ends on Enter, on the tick, on Escape, when its field is left, or
   * when the menu closes, and one end can bring on another: Enter and Escape
   * take the field away while it has the focus, and the browser may then report
   * the field as left. Only the first end counts, so a name kept by Enter is not
   * dropped again by the field going.
   */
  const ended = useRef(false);
  /** Whether the menu is open, for a refusal that comes back after it has closed. */
  const shown = useRef(false);

  const nameOf = (folder: InvoiceFolder) => (sent?.id === folder.id ? sent.name : folder.name);

  const chosen = folders.find((folder) => folder.id === value);
  const label = chosen ? nameOf(chosen) : t('invoices.folders');

  const show = () => {
    shown.current = true;
    setOpen(true);
  };

  /*
   * 1.10 — a name typed over a folder is kept by Enter or the tick, and by
   * nothing else. Leaving the field used to keep it too, so a name started and
   * then abandoned — the menu closed, another row pressed — became the folder's
   * name, and the old one was gone without anyone having saved anything.
   */
  const endRename = (keep: boolean) => {
    if (!renaming || ended.current) return;
    ended.current = true;
    setRenaming(null);
    const { folder } = renaming;
    const name = renaming.name.trim();
    // An emptied name would only be refused, and the name it already has would
    // change nothing, so neither is sent.
    if (!keep || !name || name === folder.name) return;
    setSent({ id: folder.id, name });
    void onRename(folder, name)
      .catch((failure: unknown) => {
        const code = failure instanceof RequestError ? failure.code : 'errors.unknown';
        // Under its folder while the menu is open. Once it has closed there is
        // no row to show it under, and silence would read as a rename that took.
        if (shown.current) setRefused({ id: folder.id, code });
        else toast.error(t(code));
      })
      .finally(() => setSent((was) => (was?.id === folder.id ? null : was)));
  };

  const close = () => {
    // Closing the menu over a name being typed puts the old name back, and
    // drops a new folder's name before anything is made. Every way the menu
    // closes comes through here.
    endRename(false);
    setCreating(null);
    shown.current = false;
    setOpen(false);
    setRefused(null);
  };

  const startRename = (folder: InvoiceFolder) => {
    // A name still being typed for another folder is dropped, as leaving its
    // field drops it — and dropped now, before this rename begins, so that
    // field going later cannot end this rename instead of its own.
    field.current?.blur();
    // One field at a time, unless a new folder is already on its way.
    if (!making) setCreating(null);
    ended.current = false;
    setRefused(null);
    setRenaming({ folder, name: folder.name });
  };

  const startCreate = () => {
    field.current?.blur();
    setRefused(null);
    setCreating('');
  };

  /*
   * 1.10 — the new folder's field ends as a rename does: Enter or the tick makes
   * the folder, and leaving the field, Escape or closing the menu drops the name.
   * The name is checked as the manage dialog checks one, and stays in its field
   * until the server has answered, so a refused name is there to be corrected.
   */
  const endCreate = (keep: boolean) => {
    if (creating === null || making) return;
    if (!keep || !creating.trim()) {
      setCreating(null);
      setRefused(null);
      return;
    }
    const parsed = invoiceFolderSchema.safeParse({ name: creating });
    if (!parsed.success) {
      setRefused({ id: NEW_FOLDER, code: parsed.error.issues[0]?.message ?? 'errors.validation' });
      return;
    }
    setRefused(null);
    setMaking(true);
    void onCreate(parsed.data.name)
      .then(() => setCreating(null))
      .catch((failure: unknown) => {
        const code = failure instanceof RequestError ? failure.code : 'errors.unknown';
        // Under the field while the menu is open, as a refused rename is.
        if (shown.current) setRefused({ id: NEW_FOLDER, code });
        else toast.error(t(code));
      })
      .finally(() => setMaking(false));
  };

  return (
    <>
      <button
        type="button"
        ref={anchor}
        onClick={() => (open ? close() : show())}
        aria-expanded={open}
        aria-label={t('invoices.folders')}
        className={cn(
          'h-9 px-3 shrink-0 max-w-[45%] inline-flex items-center gap-1.5 rounded-lg text-sm transition-colors',
          chosen ? 'bg-primary/15 text-primary' : 'bg-muted hover:bg-muted/70',
        )}
      >
        {/* The icon carries the heading, the way the period picker's calendar
            does beside it — a row of filters that each spell out what they
            filter is a row where the chosen values have no width left. The
            label stays, because "which folder" is the answer someone is
            reading back off this control. */}
        <Folder className={cn('h-4 w-4 shrink-0', chosen ? 'text-primary' : 'text-muted-foreground')} />
        <span className="truncate">{label}</span>
        <ChevronDown className={cn('h-4 w-4 shrink-0 opacity-60 transition-transform', open && 'rotate-180')} />
      </button>
      <AnchoredMenu open={open} onClose={close} anchorRef={anchor} width={260}>
        <div className="max-h-[60vh] overflow-y-auto py-1">
          {/* 1.10 — "תיקיות" takes the choice away, so the list is every invoice
              again and the control says "תיקיות" too. The plus is a button of
              its own beside it, as the pencil is beside a folder: it opens a
              field for the new folder's name, so it neither picks nor closes. */}
          <div className="flex items-center pe-2">
            <button
              type="button"
              onClick={() => {
                onChange('');
                close();
              }}
              className={cn(menuItem, 'flex-1 min-w-0 hover:bg-muted', !chosen && 'text-primary')}
            >
              {t('invoices.folders')}
            </button>
            <button
              type="button"
              disabled={making}
              onClick={startCreate}
              aria-label={t('invoices.createFolderAction')}
              title={t('invoices.createFolderAction')}
              className="p-1.5 shrink-0 rounded-lg text-primary hover:bg-muted transition-colors disabled:opacity-30"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
          {creating !== null && (
            <div className="flex items-center pe-2">
              {/* Read-only rather than disabled while the name is on its way: a
                  disabled field gives up the focus, and the keyboard with it. */}
              <Input
                autoFocus
                value={creating}
                readOnly={making}
                enterKeyHint="done"
                placeholder={t('invoices.folderName')}
                aria-label={t('invoices.newFolder')}
                onChange={(event) => setCreating(event.target.value)}
                onBlur={() => endCreate(false)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') endCreate(true);
                  if (event.key === 'Escape') endCreate(false);
                }}
                className="h-9 my-0.5 ms-1 flex-1 min-w-0"
              />
              <button
                type="button"
                disabled={making}
                // Pressing the tick would take the focus from the field before
                // the tick is clicked, and leaving the field drops the name.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => endCreate(true)}
                aria-label={t('common.save')}
                title={t('common.save')}
                className="p-1.5 shrink-0 rounded-lg text-primary hover:bg-muted transition-colors disabled:opacity-30"
              >
                <Check className="h-4 w-4" />
              </button>
            </div>
          )}
          {refused?.id === NEW_FOLDER && (
            <div className="px-3 pb-2">
              <ErrorNotice code={refused.code} />
            </div>
          )}
          {/* Before the first folder exists the menu would otherwise be a lone
              row that means "everything", which reads as a control that failed
              to load rather than as one with nothing to offer yet. */}
          {folders.length === 0 && (
            <p className="px-3 py-2.5 text-sm text-muted-foreground">{t('invoices.noFoldersYet')}</p>
          )}
          {folders.map((folder) => {
            const draft = renaming?.folder.id === folder.id ? renaming : null;
            return (
              <div key={folder.id}>
                <div className={cn('flex items-center pe-2', value === folder.id && 'bg-muted/50')}>
                  {draft ? (
                    /* 1.3 — the name, typed over where it stands, with a tick
                       beside it to keep it. */
                    <>
                      <Input
                        ref={field}
                        autoFocus
                        value={draft.name}
                        enterKeyHint="done"
                        aria-label={t('invoices.folderName')}
                        onChange={(event) => setRenaming({ folder, name: event.target.value })}
                        onBlur={() => endRename(false)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') endRename(true);
                          if (event.key === 'Escape') endRename(false);
                        }}
                        className="h-9 my-0.5 ms-1 flex-1 min-w-0"
                      />
                      <button
                        type="button"
                        // Pressing the tick would take the focus from the field
                        // before the tick is clicked, and leaving the field puts
                        // the old name back.
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => endRename(true)}
                        aria-label={t('common.save')}
                        title={t('common.save')}
                        className="p-1.5 shrink-0 rounded-lg text-primary hover:bg-muted transition-colors"
                      >
                        <Check className="h-4 w-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          onChange(folder.id);
                          close();
                        }}
                        className={cn(menuItem, 'flex-1 min-w-0 hover:bg-muted', value === folder.id && 'text-primary')}
                      >
                        <span className="truncate">{nameOf(folder)}</span>
                        <span className="ms-auto shrink-0 text-xs text-muted-foreground ltr-nums">{folder.count}</span>
                      </button>
                      {/* 1.3 — a button of its own, as the share icon beside it
                          is and for the same reason: pressing it is not pressing
                          the folder, so it neither filters the list nor closes the
                          menu. Always drawn, because a phone has no pointer to
                          hover with, and held back only while a new name is on
                          its way. */}
                      <button
                        type="button"
                        disabled={sent?.id === folder.id}
                        onClick={() => startRename(folder)}
                        aria-label={t('invoices.renameFolderAction')}
                        title={t('invoices.renameFolderAction')}
                        className="p-1.5 shrink-0 rounded-lg text-muted-foreground hover:bg-muted transition-colors disabled:opacity-30"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      {/* Its own button rather than part of the row: pressing the name
                          filters the list to the folder, pressing this hands the folder
                          over. Nothing to send from an empty one. */}
                      <button
                        type="button"
                        disabled={folder.count === 0 || sharing}
                        onClick={() => {
                          onShare(folder);
                          close();
                        }}
                        aria-label={t('invoices.shareFolderAction')}
                        title={t('invoices.shareFolderAction')}
                        className="p-1.5 shrink-0 rounded-lg text-primary hover:bg-muted transition-colors disabled:opacity-30"
                      >
                        <Share2 className="h-4 w-4" />
                      </button>
                      {/* 10.1 — a button of its own too, and always drawn. It
                          closes the menu, because the question it asks opens
                          over the screen and the menu would float above it;
                          the page asks, and deletes nothing unless agreed to. */}
                      <button
                        type="button"
                        disabled={sent?.id === folder.id}
                        onClick={() => {
                          onDelete(folder);
                          close();
                        }}
                        aria-label={t('invoices.deleteFolder')}
                        title={t('invoices.deleteFolder')}
                        className="p-1.5 shrink-0 rounded-lg text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-30"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
                {/* 1.8 — when the folder was last handed over, under its row,
                    and only for a folder that has been. */}
                {folder.lastShare && (
                  <p className="px-3 pb-2 text-xs text-muted-foreground">
                    {t('invoices.folderSentAt', {
                      date: formatDate(folder.lastShare.sentAt, locale),
                      time: formatTime(folder.lastShare.sentAt, locale),
                    })}
                  </p>
                )}
                {refused?.id === folder.id && (
                  <div className="px-3 pb-2">
                    <ErrorNotice code={refused.code} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </AnchoredMenu>
    </>
  );
}

/**
 * 1.5 — what the doc-type filter offers, in the order Nitzan listed it, and the
 * types each choice stands for.
 *
 * Five choices over nine types and the rows with none. Two of them are a pair
 * the scan tells apart and a person does not: the two tax invoices, and the two
 * English ones. The last is everything Worki has no name for — `other`, which
 * it could not read, and `unset`, which it never read — because to the one
 * asking both are "not identified".
 *
 * A payment demand and a "not yet a tax invoice" are in none of them. They are
 * not gone: they are on the list while no choice is ticked, and leave it when
 * one is.
 */
const DOC_TYPE_OPTIONS = [
  { key: 'taxInvoiceReceipt', types: ['tax_invoice', 'tax_invoice_receipt'] },
  { key: 'receipt', types: ['receipt'] },
  { key: 'creditNote', types: ['credit_note'] },
  { key: 'english', types: ['en_tax_invoice', 'en_invoice'] },
  { key: 'unrecognised', types: ['other', INVOICE_DOC_TYPE_UNSET] },
] as const satisfies readonly { key: string; types: readonly InvoiceDocTypeFilter[] }[];

type DocTypeOption = (typeof DOC_TYPE_OPTIONS)[number]['key'];

/**
 * What the API is asked for when `chosen` are ticked: their types, in the
 * table's order, so the same boxes ticked in another order ask the same thing.
 * Nothing ticked asks for nothing, which the API reads as every type.
 */
function docTypeParamOf(chosen: ReadonlySet<DocTypeOption>): string {
  return DOC_TYPE_OPTIONS.filter(({ key }) => chosen.has(key))
    .flatMap(({ types }) => types)
    .join(',');
}

/**
 * INV-007 — the doc-type filter: a set of boxes, not a `<select>`.
 *
 * A select would answer one choice at a time, and what is asked for is often
 * two — the tax invoices and the receipts. 1.5 — the boxes are the five choices
 * in `DOC_TYPE_OPTIONS`, not every type the scan can tell apart.
 */
function DocTypeFilter({
  value,
  onToggle,
  onClear,
}: {
  value: ReadonlySet<DocTypeOption>;
  /*
   * One box, not the whole set. The set math belongs to the owner of the state
   * so it can be done against the latest value: computing `new Set(value)` here
   * reads the set this render was given, and two boxes toggled inside one
   * render — a double tap, or a phone that delivers two taps in one batch —
   * both start from it, so the second undoes the first. Measured: ticking two
   * in the same tick left one ticked and the list showing a single invoice.
   */
  onToggle: (option: DocTypeOption) => void;
  onClear: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLButtonElement>(null);

  const label =
    value.size === 0
      ? t('invoices.docTypeFilter')
      : value.size === 1
        ? t(`invoices.docTypeOption.${[...value][0]}`)
        : t('invoices.docTypeSelected', { count: value.size });

  const row = (option: DocTypeOption) => {
    const on = value.has(option);
    return (
      <button
        key={option}
        type="button"
        role="menuitemcheckbox"
        aria-checked={on}
        onClick={() => onToggle(option)}
        className={cn(menuItem, 'hover:bg-muted')}
      >
        {/* The box is drawn rather than an `<input type="checkbox">`: the row
            is the hit target, and a real checkbox inside it would take half
            the clicks and toggle twice on the other half. */}
        <span
          aria-hidden
          className={cn(
            'h-4 w-4 shrink-0 grid place-items-center rounded border transition-colors',
            on ? 'bg-primary border-primary text-primary-foreground' : 'border-border',
          )}
        >
          {on && <Check className="h-3 w-3" strokeWidth={3} />}
        </span>
        <span className="min-w-0 truncate">{t(`invoices.docTypeOption.${option}`)}</span>
      </button>
    );
  };

  return (
    <>
      <button
        type="button"
        ref={anchor}
        onClick={() => setOpen((was) => !was)}
        aria-expanded={open}
        className={cn(
          'h-9 px-3 shrink-0 max-w-[45%] inline-flex items-center gap-1.5 rounded-lg text-sm transition-colors',
          value.size > 0 ? 'bg-primary/15 text-primary' : 'bg-muted hover:bg-muted/70',
        )}
      >
        <span className="truncate">{label}</span>
        <ChevronDown className="h-4 w-4 shrink-0 opacity-60" />
      </button>
      <AnchoredMenu open={open} onClose={() => setOpen(false)} anchorRef={anchor} width={232}>
        <div className="max-h-[60vh] overflow-y-auto py-1">
          {DOC_TYPE_OPTIONS.map(({ key }) => row(key))}
        </div>
        {value.size > 0 && (
          <button
            type="button"
            onClick={onClear}
            className={cn(menuItem, 'hover:bg-muted border-t border-border text-muted-foreground')}
          >
            <X className="h-4 w-4 shrink-0" />
            {t('invoices.docTypeFilterAll')}
          </button>
        )}
      </AnchoredMenu>
    </>
  );
}

function DocTypeBadge({ docType }: { docType: InvoiceDocType | null }) {
  const { t } = useTranslation();
  if (docType === null) return null;
  if (DEDUCTIBLE_DOC_TYPES.includes(docType)) return null;
  // `other` is Worki failing to read the document, not the document failing to
  // be a tax invoice, and the two must not wear the same badge — one is a
  // supplier's problem and the other is ours.
  const tone = docType === 'other' ? 'neutral' : 'warning';
  return (
    <Badge tone={tone}>
      <FileQuestion className="h-3 w-3 shrink-0" aria-hidden />
      {t(`invoices.docType.${docType}`)}
    </Badge>
  );
}

const IMAP_PRESETS = {
  gmail: { host: 'imap.gmail.com', port: 993 },
  outlook: { host: 'outlook.office365.com', port: 993 },
  custom: { host: '', port: 993 },
} as const;

type ImapPreset = keyof typeof IMAP_PRESETS;

/** INV-001 / INV-002 — the connect step, with the 2-account cap surfaced as a field error. */
function ConnectAccountDialog({
  open,
  driver,
  oauthProviders,
  onClose,
  onConnected,
}: {
  open: boolean;
  driver: string | undefined;
  /** Which "connect with…" buttons this deployment has clients configured for. */
  oauthProviders: string[];
  onClose: () => void;
  onConnected: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [preset, setPreset] = useState<ImapPreset>('gmail');
  const [host, setHost] = useState<string>(IMAP_PRESETS.gmail.host);
  const [port, setPort] = useState<string>(String(IMAP_PRESETS.gmail.port));
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // The demo driver connects any address instantly; only the real IMAP driver
  // needs a mailbox host and an app password.
  const needsCredentials = driver === 'imap';

  useEffect(() => {
    if (open) {
      setEmail('');
      setPreset('gmail');
      setHost(IMAP_PRESETS.gmail.host);
      setPort(String(IMAP_PRESETS.gmail.port));
      setPassword('');
      setError(null);
    }
  }, [open]);

  const selectPreset = (next: ImapPreset) => {
    setPreset(next);
    setHost(IMAP_PRESETS[next].host);
    setPort(String(IMAP_PRESETS[next].port));
  };

  /*
   * INV-008 — leaving for the provider by whichever route this shell has.
   *
   * On the web the anchor is the whole flow. A packaged shell cannot navigate
   * to it: its session is a Bearer token, so `authGuard` sees no cookie and
   * answers 401 — which the WebView renders as a page of raw JSON — and Google
   * refuses consent inside an embedded WebView in any case. So the shell asks
   * for the URL with the credentials it does have and hands it to the system
   * browser. Nothing is lost by leaving: the callback takes identity from the
   * flow row rather than from a cookie the system browser never carried.
   */
  const [opening, setOpening] = useState<string | null>(null);
  const startOauth = async (provider: string) => {
    setOpening(provider);
    setError(null);
    try {
      const { url } = await api.get<{ url: string }>(`/invoices/accounts/oauth/${provider}/url`);
      await platform.openExternal(url);
      onClose();
    } catch (failure) {
      setError(failure instanceof RequestError ? failure.code : 'invoices.oauthFailed');
    } finally {
      setOpening(null);
    }
  };

  const oauthButton = (provider: 'google' | 'microsoft', mark: ReactNode, label: string) =>
    platform.mailboxOauth === 'redirect' ? (
      <a href={apiUrl(`/invoices/accounts/oauth/${provider}/start`)} className={OAUTH_BUTTON}>
        {mark}
        {label}
      </a>
    ) : (
      <button
        type="button"
        className={OAUTH_BUTTON}
        disabled={opening !== null}
        onClick={() => void startOauth(provider)}
      >
        {mark}
        {opening === provider ? t('common.loading') : label}
      </button>
    );

  const emailError = error?.startsWith('validation.email') ? error : null;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'validation.email.invalid');
      return;
    }
    if (needsCredentials && (host.trim() === '' || password === '')) {
      setError('invoices.imapCredentialsRequired');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await api.post('/invoices/accounts', {
        email: parsed.data,
        ...(needsCredentials
          ? {
              host: host.trim(),
              port: Number(port) || 993,
              secure: true,
              username: parsed.data,
              password,
            }
          : {}),
      });
      toast.success(t('invoices.accountConnected'));
      onConnected();
      onClose();
    } catch (err) {
      setError(err instanceof RequestError ? err.code : 'errors.unknown');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={t('invoices.connectAccount')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" loading={submitting} onClick={handleSubmit}>
            {t('invoices.connectAccount')}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="stack" noValidate>
        {error && <ErrorNotice code={error} />}
        {!needsCredentials && <p className="subtle">{t('invoices.demoProviderNote')}</p>}

        {/*
          * INV-008 — the way in that does not ask for a password.
          *
          * A link on the web: the user has to leave for the provider's own
          * screen and come back to a callback on this origin, which only a real
          * navigation does. In a packaged shell the same trip is made by the
          * system browser instead — see `startOauth`. The app-password form
          * stays underneath for every mailbox that is neither — a company's own
          * IMAP server, or a provider Worki has no client for.
          */}
        {oauthProviders.length > 0 && (
          <div className="stack">
            <p className="subtle">{t('invoices.oauthHint')}</p>
            <div className="flex flex-col gap-2">
              {oauthProviders.includes('google') &&
                oauthButton('google', <GoogleMark />, t('invoices.connectWithGoogle'))}
              {oauthProviders.includes('microsoft') &&
                oauthButton('microsoft', <MicrosoftMark />, t('invoices.connectWithMicrosoft'))}
            </div>
            <div className="flex items-center gap-3" aria-hidden>
              <span className="h-px flex-1 bg-border" />
              <span className="text-[11px] text-muted-foreground">{t('invoices.orAppPassword')}</span>
              <span className="h-px flex-1 bg-border" />
            </div>
          </div>
        )}
        {/*
         * Only a malformed address belongs under the email field. A rejected
         * app password is about the mailbox, not this input, and printing it
         * twice — once in the notice, once here — read as two failures.
         */}
        <Field
          label={t('customer.email')}
          htmlFor="connect-email"
          {...(emailError ? { error: t(emailError) } : {})}
        >
          <Input
            id="connect-email"
            type="email"
            dir="ltr"
            autoFocus
            value={email}
            invalid={Boolean(emailError)}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>

        {needsCredentials && (
          <>
            <Field label={t('invoices.mailProvider')} htmlFor="connect-preset">
              <Select
                id="connect-preset"
                value={preset}
                onChange={(value) => selectPreset(value as ImapPreset)}
              >
                <option value="gmail">Gmail</option>
                <option value="outlook">Outlook / Microsoft 365</option>
                <option value="custom">{t('invoices.customImap')}</option>
              </Select>
            </Field>

            <div className="form-grid">
              <Field label={t('invoices.imapHost')} htmlFor="connect-host">
                <Input
                  id="connect-host"
                  dir="ltr"
                  value={host}
                  readOnly={preset !== 'custom'}
                  onChange={(event) => setHost(event.target.value)}
                />
              </Field>
              <Field label={t('invoices.imapPort')} htmlFor="connect-port">
                <Input
                  id="connect-port"
                  dir="ltr"
                  inputMode="numeric"
                  value={port}
                  readOnly={preset !== 'custom'}
                  onChange={(event) => setPort(event.target.value)}
                />
              </Field>
            </div>

            <Field
              label={t('invoices.appPassword')}
              htmlFor="connect-password"
              hint={t('invoices.appPasswordHint')}
            >
              <Input
                id="connect-password"
                type="password"
                dir="ltr"
                autoComplete="off"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </Field>
          </>
        )}
      </form>
    </Modal>
  );
}
